/**
 * ImmeublePortal — PMS (Property Management System) côté client.
 *
 * ⚠️ ARCHITECTURE — Branché sur le MODULE OPÉRATIONNEL (routes/portal_immeuble.py)
 *    Collections MongoDB : properties, units, tenants, leases, tickets,
 *    mileage_logs, drops, safe_documents.
 *    ❌ ZÉRO lien avec la collection déclarative `immeuble_dossiers`.
 *
 * Étape 1 wiring — Live data depuis GET /api/portal/immeuble/dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Building2, DoorOpen, ChevronDown, ChevronRight, Plus, X, FileText, FileDown, Lock, Upload, FileSignature, Banknote, Receipt, MapPin, Users, Wrench, BarChart3, Sparkles, ArrowRight, ShieldCheck, CheckCircle2, Clock, AlertCircle, TrendingUp, Calendar, Pencil, Loader2, RefreshCw, Trash2, FolderOpen, Inbox, BookOpen, ReceiptText, Smartphone } from "lucide-react";
import { toast } from "@/lib/toast";

import { EmptyHint, ModalStyles } from "./modals/shared";
import { StripeActionModal } from "./modals/StripeActionModal";
import { Rl31ActionModal } from "./modals/Rl31ActionModal";
import { MileageActionModal } from "./modals/MileageActionModal";
import { CreditCheckActionModal } from "./modals/CreditCheckActionModal";
import { RentIncreaseActionModal } from "./modals/RentIncreaseActionModal";
import { JournalActionModal } from "./modals/JournalActionModal";
import { ReceiptsActionModal } from "./modals/ReceiptsActionModal";
// Batch #14.226 — Édition de la répartition du loyer (multi-locataires)
import { RentSplitEditor } from "@/components/RentSplitEditor";
import { TalCalculator } from "@/components/TalCalculator";
import { RentArrearsBoard } from "@/components/RentArrearsBoard";
import { API, portalAuthH as authH } from "@/api";


// =====================================================================
//  Helpers — mapping backend → frontend
// =====================================================================

/** Formate une adresse depuis un doc property (string libre ou champs séparés). */
function formatAddress(prop) {
  if (!prop) return "";
  if (typeof prop.address_line === "string" && prop.address_line.trim()) {
    const parts = [prop.address_line, prop.city, prop.province, prop.postal_code].filter(Boolean);
    return parts.join(", ");
  }
  if (typeof prop.address === "string" && prop.address.trim()) return prop.address;
  const parts = [
    [prop.civic_number, prop.street].filter(Boolean).join(" "),
    prop.city,
    prop.province,
    prop.postal_code,
  ].filter(Boolean);
  return parts.join(", ");
}

/** Transforme la réponse `/portal/immeuble/dashboard` en buildings + apartmentsByBuildingId */
// Batch #14.150 — exporté pour PortalImmeubleActionPage (réutilisation)
export function mapDashboard(data) {
  const properties = data?.properties || [];
  const units = data?.units || [];

  const apartmentsByBuilding = {};
  units.forEach((u) => {
    const apt = {
      id: u.id,
      apt_number: u.unit_label || "?",
      bedrooms: typeof u.bedrooms === "number" ? u.bedrooms : null,
      monthly_rent: typeof u.monthly_rent === "number" ? u.monthly_rent : 0,
      tenants_count: u.tenant_count ?? 0,
      // Batch #14.191 — Source unique : statut occupation 3 états + compteur locataires actifs
      occupancy_status: u.effective_occupancy_status || (u.lease_status === "occupied" ? "occupied" : "vacant"),
      occupancy_status_manual: u.occupancy_status || null, // null = auto, sinon manuel
      occupancy_notes: u.occupancy_notes || "",
      active_tenants_count: u.active_tenants_count ?? 0,
      has_active_lease: u.lease_status === "occupied", // legacy, conservé pour ne rien casser
      property_id: u.property_id,
      // Étape 3 — Données réelles tenants & tickets
      unit_tenants: u.unit_tenants || [],
      unit_tickets: u.unit_tickets || [],
      tickets_open_count: u.tickets_open_count ?? 0,
      tickets_in_progress_count: u.tickets_in_progress_count ?? 0,
      tickets_resolved_count: u.tickets_resolved_count ?? 0,
      // Étape 3.5 — Paiements de loyer (année courante)
      unit_rent_payments: u.unit_rent_payments || [],
      rent_paid_count: u.rent_paid_count ?? 0,
      rent_late_count: u.rent_late_count ?? 0,
      rent_due_count: u.rent_due_count ?? 0,
      // Batch #14.226 — Bail actif (pour édition de la répartition du loyer)
      active_leases: u.active_leases || [],
      _raw: u, // Conservé pour pré-remplir la modale d'édition
    };
    if (!apartmentsByBuilding[u.property_id]) apartmentsByBuilding[u.property_id] = [];
    apartmentsByBuilding[u.property_id].push(apt);
  });

  const buildings = properties.map((p) => {
    const aptList = apartmentsByBuilding[p.id] || [];
    // Batch #14.191 — Occupé = effective_occupancy_status === "occupied"
    const leasesActive = aptList.filter((a) => a.occupancy_status === "occupied").length;
    const monthlyRevenue = aptList
      .filter((a) => a.occupancy_status === "occupied")
      .reduce((s, a) => s + (a.monthly_rent || 0), 0);
    return {
      id: p.id,
      name: p.name || "(Immeuble sans nom)",
      address: formatAddress(p),
      units_count: aptList.length,
      leases_active: leasesActive,
      monthly_revenue: monthlyRevenue,
      _raw: p,
    };
  });

  return { buildings, apartmentsByBuilding };
}

// =====================================================================
// Batch #14.191 — Badge statut occupation (3 états)
// =====================================================================
function OccupancyBadge({ apt }) {
  const status = apt?.occupancy_status || "vacant";
  const isManual = apt?.occupancy_status_manual != null;
  if (status === "occupied") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200"
        title={isManual ? "Statut manuel : Occupé" : "Auto-détecté Occupé (≥1 locataire actif)"}
        data-testid={`apt-occupancy-occupied-${apt?.id}`}
      >
        🟢 Occupé{isManual && <span className="text-[9px]">(M)</span>}
      </span>
    );
  }
  if (status === "transition") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700 border border-amber-200"
        title="Statut manuel : En transition (entre 2 locataires)"
        data-testid={`apt-occupancy-transition-${apt?.id}`}
      >
        🟡 Transition<span className="text-[9px]">(M)</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-100 text-rose-700 border border-rose-200"
      title={isManual ? "Statut manuel : Vacant" : "Auto-détecté Vacant (aucun locataire actif)"}
      data-testid={`apt-occupancy-vacant-${apt?.id}`}
    >
      🔴 Vacant{isManual && <span className="text-[9px]">(M)</span>}
    </span>
  );
}

// =====================================================================

export default function ImmeublePortal() {
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [selectedAptId, setSelectedAptId] = useState(null);
  const [buildingModal, setBuildingModal] = useState(null); // null | { mode: "new"|"edit", target? }
  const [aptModal, setAptModal] = useState(null); // null | { mode, target?, buildingId }

  // Étape 1 wiring — Live data depuis /api/portal/immeuble/dashboard
  const [buildings, setBuildings] = useState([]);
  const [apartmentsByBuilding, setApartmentsByBuilding] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/portal/immeuble/dashboard`, { headers: authH() });
        if (cancelled) return;
        const { buildings: bs, apartmentsByBuilding: abb } = mapDashboard(r.data);
        setBuildings(bs);
        setApartmentsByBuilding(abb);
      } catch (e) {
        if (cancelled) return;
        const detail = e.response?.data?.detail || e.message || "Erreur de chargement";
        setError(detail);
        toast.error(detail);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const apartments = selectedBuildingId ? (apartmentsByBuilding[selectedBuildingId] || []) : [];
  // Batch #14.9 — Tous les apartments à plat pour les modales qui ont leur propre selector immeuble
  // (ex: Conciliation manuelle). Sinon apartments filtré bloque la cascade.
  const allApartments = useMemo(
    () => Object.values(apartmentsByBuilding).flat(),
    [apartmentsByBuilding],
  );
  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId) || null;
  const selectedApt = apartments.find((a) => a.id === selectedAptId) || null;

  const totalBuildings = buildings.length;
  const totalApts = buildings.reduce((s, b) => s + b.units_count, 0);
  const totalActiveLeases = buildings.reduce((s, b) => s + b.leases_active, 0);
  const totalRevenue = buildings.reduce((s, b) => s + b.monthly_revenue, 0);

  const onBuildingChange = (id) => {
    setSelectedBuildingId(id || null);
    setSelectedAptId(null);
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-100px)] -mx-4 -mb-4 sm:-mx-6 lg:-mx-8" data-testid="immeuble-portal">
      {/* ============= HEADER ============= */}
      <div className="bg-gradient-to-r from-indigo-50 via-white to-emerald-50 border-b border-slate-200 px-6 py-5">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-sm">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" data-testid="immeuble-portal-title">
                Gestion immeuble
              </h1>
              <Breadcrumb building={selectedBuilding} apt={selectedApt} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {loading && (
              <span className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 bg-white/70 px-2.5 py-1 rounded-full border border-slate-200" data-testid="immeuble-loading-badge">
                <Loader2 className="w-3 h-3 animate-spin" /> Chargement…
              </span>
            )}
            {error && !loading && (
              <span className="text-[11px] text-rose-700 inline-flex items-center gap-1.5 bg-rose-50 px-2.5 py-1 rounded-full border border-rose-200" data-testid="immeuble-error-badge">
                <AlertCircle className="w-3 h-3" /> Erreur de chargement
              </span>
            )}
            <button
              onClick={reload}
              disabled={loading}
              className="text-[11px] text-slate-600 inline-flex items-center gap-1.5 bg-white px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
              title="Recharger les données"
              data-testid="immeuble-reload-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
            </button>
          </div>
        </div>
      </div>

      {/* ============= BODY ============= */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-4 lg:py-6">
        {/* --- BARRE MOBILE compacte (selectors + add buttons) — visible < lg --- */}
        <MobileSelectorBar
          buildings={buildings}
          apartments={apartments}
          selectedBuildingId={selectedBuildingId}
          selectedAptId={selectedAptId}
          onBuildingChange={onBuildingChange}
          onAptChange={setSelectedAptId}
          onAddBuilding={() => setBuildingModal({ mode: "new" })}
          onAddApt={() => setAptModal({ mode: "new", buildingId: selectedBuildingId })}
        />

        <div className="flex gap-6 items-start flex-col lg:flex-row">
          {/* ----- COLONNE GAUCHE — desktop uniquement (lg+) ----- */}
          <aside className="hidden lg:block lg:w-80 lg:flex-shrink-0 lg:sticky lg:top-4 space-y-4 self-start" data-testid="immeuble-portal-left">
            <ActionCard
              onAddBuilding={() => setBuildingModal({ mode: "new" })}
              onAddApt={() => setAptModal({ mode: "new", buildingId: selectedBuildingId })}
              canAddApt={!!selectedBuildingId}
            />
            <SelectorCard
              buildings={buildings}
              apartments={apartments}
              selectedBuildingId={selectedBuildingId}
              selectedAptId={selectedAptId}
              onBuildingChange={onBuildingChange}
              onAptChange={setSelectedAptId}
            />
            <StatsCard
              totalBuildings={totalBuildings}
              totalApts={totalApts}
              totalLeases={totalActiveLeases}
            />
          </aside>

          {/* ----- COLONNE DROITE — Structure « Deux Blocs » ----- */}
          <section className="flex-1 min-w-0 space-y-4 w-full" data-testid="immeuble-portal-right">
            {loading ? (
              <LoadingTopBlock />
            ) : error ? (
              <ErrorTopBlock detail={error} onRetry={reload} />
            ) : buildings.length === 0 ? (
              <EmptyTopBlock onAddBuilding={() => setBuildingModal({ mode: "new" })} />
            ) : (
              <TopBlock
                selectedBuilding={selectedBuilding}
                apartments={apartments}
                selectedApt={selectedApt}
                onSelectApt={setSelectedAptId}
                onClearApt={() => setSelectedAptId(null)}
                onEditBuilding={() => setBuildingModal({ mode: "edit", target: selectedBuilding })}
                onEditApt={() => setAptModal({ mode: "edit", target: selectedApt, buildingId: selectedBuildingId })}
                totalBuildings={totalBuildings}
                totalApts={totalApts}
                totalLeases={totalActiveLeases}
                totalRevenue={totalRevenue}
                onReload={reload}
              />
            )}

            {/* ===== BLOC DU BAS — Bloc Fixe (TOUJOURS visible) ===== */}
            <BottomFixedBlock
              buildings={buildings}
              apartments={apartments}
              allApartments={allApartments}
              selectedBuilding={selectedBuilding}
              selectedApt={selectedApt}
              onBuildingChange={onBuildingChange}
              onAptChange={setSelectedAptId}
              onReload={reload}
            />
          </section>
        </div>
      </div>

      {/* Modals de gestion immeuble / appartement — Étape 2 wiring */}
      {buildingModal && (
        <BuildingFormModal
          mode={buildingModal.mode}
          target={buildingModal.target}
          onClose={() => setBuildingModal(null)}
          onSaved={() => {
            setBuildingModal(null);
            reload();
          }}
        />
      )}
      {aptModal && (
        <UnitFormModal
          mode={aptModal.mode}
          target={aptModal.target}
          buildingId={aptModal.buildingId}
          buildings={buildings}
          onClose={() => setAptModal(null)}
          onSaved={() => {
            setAptModal(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
//  HEADER · Breadcrumb
// =====================================================================
function Breadcrumb({ building, apt }) {
  return (
    <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
      <span>Mes immeubles</span>
      {building && (
        <>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-slate-700">{building.name}</span>
        </>
      )}
      {apt && (
        <>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-slate-700">Appartement {apt.apt_number}</span>
        </>
      )}
    </div>
  );
}

// =====================================================================
//  PANEL GAUCHE · ActionCard (2 boutons création)
// =====================================================================
function ActionCard({ onAddBuilding, onAddApt, canAddApt }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid="immeuble-action-card">
      <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2.5">Actions rapides</h3>
      <div className="space-y-2">
        <button
          onClick={onAddBuilding}
          className="w-full inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold shadow-sm transition"
          data-testid="immeuble-action-add-building"
        >
          <span className="inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Gérer un immeuble</span>
          <Building2 className="w-4 h-4 opacity-70" />
        </button>
        <button
          onClick={onAddApt}
          disabled={!canAddApt}
          className="w-full inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
          title={canAddApt ? "Ajouter un appartement à l'immeuble sélectionné" : "Sélectionnez d'abord un immeuble"}
          data-testid="immeuble-action-add-apt"
        >
          <span className="inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Gérer un appartement</span>
          <DoorOpen className="w-4 h-4 opacity-70" />
        </button>
        {!canAddApt && (
          <p className="text-[10px] text-slate-400 italic mt-1.5 px-1">
            💡 Sélectionnez un immeuble pour activer la création d'appartement
          </p>
        )}
      </div>
    </div>
  );
}

// =====================================================================
//  PANEL GAUCHE · SelectorCard (2 dropdowns cascade)
// =====================================================================
function SelectorCard({ buildings, apartments, selectedBuildingId, selectedAptId, onBuildingChange, onAptChange }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid="immeuble-selector-card">
      <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2.5">Navigation</h3>
      <div className="space-y-3">
        <label className="block">
          <span className="text-[11px] font-medium text-slate-600 inline-flex items-center gap-1.5 mb-1">
            <Building2 className="w-3 h-3 text-indigo-500" /> Immeuble
          </span>
          <div className="relative">
            <select
              value={selectedBuildingId || ""}
              onChange={(e) => onBuildingChange(e.target.value)}
              className="w-full appearance-none px-3 py-2 pr-8 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition cursor-pointer"
              data-testid="immeuble-select-building"
            >
              <option value="">— Tous mes immeubles —</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2.5 top-2.5 text-slate-400 pointer-events-none" />
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium text-slate-600 inline-flex items-center gap-1.5 mb-1">
            <DoorOpen className="w-3 h-3 text-blue-500" /> Appartement
          </span>
          <div className="relative">
            <select
              value={selectedAptId || ""}
              onChange={(e) => onAptChange(e.target.value || null)}
              disabled={!selectedBuildingId}
              className="w-full appearance-none px-3 py-2 pr-8 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
              data-testid="immeuble-select-apt"
            >
              <option value="">— Aucun (vue immeuble) —</option>
              {apartments.map((a) => (
                <option key={a.id} value={a.id}>
                  Appartement {a.apt_number} {a.bedrooms ? `· ${a.bedrooms} ch` : ""}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2.5 top-2.5 text-slate-400 pointer-events-none" />
          </div>
          {!selectedBuildingId && (
            <p className="text-[10px] text-slate-400 italic mt-1">Sélectionnez un immeuble d'abord</p>
          )}
        </label>
      </div>
    </div>
  );
}

// =====================================================================
//  PANEL GAUCHE · StatsCard (mini résumé)
// =====================================================================
function StatsCard({ totalBuildings, totalApts, totalLeases }) {
  return (
    <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-2xl p-4 shadow-sm" data-testid="immeuble-stats-card">
      <h3 className="text-[10px] font-semibold uppercase tracking-wider opacity-80 mb-3">Mon parc en bref</h3>
      <div className="space-y-2.5">
        <MiniStat icon={Building2} label="Immeubles" value={totalBuildings} />
        <MiniStat icon={DoorOpen} label="Appartements" value={totalApts} />
        <MiniStat icon={Banknote} label="Baux actifs" value={totalLeases} />
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="inline-flex items-center gap-2 opacity-90">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
      <span className="font-bold text-lg">{value}</span>
    </div>
  );
}

// =====================================================================
//  PANEL DROIT · DashboardGlobal (rien de sélectionné)
// =====================================================================
function DashboardGlobal({ totalBuildings, totalApts, totalLeases, totalRevenue }) {
  return (
    <div className="space-y-4" data-testid="immeuble-dashboard-global">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800">Tableau de bord global</h2>
            <p className="text-xs text-slate-500">Aperçu de votre portefeuille immobilier</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard tone="indigo" icon={Building2} label="Immeubles" value={totalBuildings} />
          <KpiCard tone="blue" icon={DoorOpen} label="Appartements" value={totalApts} />
          <KpiCard tone="emerald" icon={Banknote} label="Baux actifs" value={totalLeases} />
          <KpiCard tone="amber" icon={TrendingUp} label="Revenu mensuel" value={`${totalRevenue.toLocaleString("fr-CA")} $`} small />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ActivityCard
          title="Documents récents"
          icon={FileText}
          tone="indigo"
          placeholder="Les derniers dépôts de documents apparaîtront ici une fois le module branché."
        />
        <ActivityCard
          title="Activité récente"
          icon={Clock}
          tone="emerald"
          placeholder="Tickets résolus, baux signés, RL-31 générés…"
        />
      </div>

      <HintBanner />
    </div>
  );
}

function KpiCard({ tone, icon: Icon, label, value, small }) {
  // Batch #14.36 — Visuel plus marqué (icône ronde + plus de couleur)
  const tones = {
    indigo:  { card: "from-indigo-50 to-white border-indigo-100",   iconBg: "bg-indigo-100 text-indigo-700",   value: "text-indigo-900" },
    blue:    { card: "from-blue-50 to-white border-blue-100",       iconBg: "bg-blue-100 text-blue-700",       value: "text-blue-900" },
    emerald: { card: "from-emerald-50 to-white border-emerald-100", iconBg: "bg-emerald-100 text-emerald-700", value: "text-emerald-900" },
    amber:   { card: "from-amber-50 to-white border-amber-100",     iconBg: "bg-amber-100 text-amber-700",     value: "text-amber-900" },
  };
  const t = tones[tone] || tones.indigo;
  return (
    <div className={`bg-gradient-to-br ${t.card} border rounded-xl p-3.5 transition hover:shadow-md`}>
      <div className={`w-9 h-9 rounded-lg ${t.iconBg} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`${small ? "text-xl" : "text-2xl"} font-bold ${t.value} leading-none`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-1">{label}</div>
    </div>
  );
}

function ActivityCard({ title, icon: Icon, tone, placeholder }) {
  const tones = {
    indigo: "bg-indigo-100 text-indigo-600",
    emerald: "bg-emerald-100 text-emerald-600",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg ${tones[tone]} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="font-semibold text-slate-800 text-sm">{title}</h3>
      </div>
      <div className="text-center py-6 px-3">
        <p className="text-xs text-slate-400 italic">{placeholder}</p>
      </div>
    </div>
  );
}

function HintBanner() {
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl p-4 text-sm text-slate-700">
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-800 mb-1">Bienvenue dans la nouvelle interface</p>
          <p className="text-xs text-slate-600">
            Sélectionnez un immeuble dans le menu de gauche pour gérer ses documents, baux TAL, prélèvements,
            et accéder aux appartements et locataires.
          </p>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
//  États asynchrones : loading / error / empty
// =====================================================================
function LoadingTopBlock() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 shadow-sm text-center" data-testid="immeuble-loading-top">
      <Loader2 className="w-7 h-7 animate-spin text-indigo-500 mx-auto mb-3" />
      <p className="text-sm text-slate-600 font-medium">Chargement de vos immeubles…</p>
      <p className="text-xs text-slate-400 mt-1">Synchronisation avec le module opérationnel</p>
    </div>
  );
}

function ErrorTopBlock({ detail, onRetry }) {
  return (
    <div className="bg-white border border-rose-200 rounded-2xl p-8 shadow-sm" data-testid="immeuble-error-top">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-800">Impossible de charger les données</h3>
          <p className="text-xs text-slate-500 mt-1">{detail}</p>
          <button
            onClick={onRetry}
            className="mt-3 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-rose-700"
            data-testid="immeuble-error-retry"
          >
            <RefreshCw className="w-3 h-3" /> Réessayer
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyTopBlock({ onAddBuilding }) {
  // Batch #14.36 — Visuel modernisé style tuiles colorées avec illustration
  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border-2 border-indigo-100 rounded-2xl p-8 shadow-sm"
      data-testid="immeuble-empty-top"
    >
      {/* Décoration circulaire en arrière-plan */}
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-100/40 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-emerald-100/40 blur-2xl pointer-events-none" />

      <div className="relative max-w-2xl mx-auto text-center">
        {/* Icône principale dans un gros cercle */}
        <div className="inline-flex w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-500 to-emerald-500 text-white items-center justify-center shadow-lg mb-4">
          <Building2 className="w-10 h-10" />
        </div>
        <h3 className="font-bold text-slate-800 text-xl sm:text-2xl">
          Bienvenue dans votre Espace Immeuble !
        </h3>
        <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">
          Tout est prêt — il ne vous reste qu'à ajouter votre premier immeuble pour commencer
          à gérer vos baux, dépôts de documents, paiements de loyers, et plus.
        </p>

        {/* Mini cards "ce que vous pourrez faire" */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-6 max-w-xl mx-auto">
          {[
            { icon: FileSignature, label: "Générer\ndes baux", color: "blue" },
            { icon: FolderOpen,    label: "Coffre-fort\ndocuments", color: "violet" },
            { icon: Banknote,      label: "Suivi des\nloyers", color: "emerald" },
            { icon: ShieldCheck,   label: "Enquêtes\nde crédit", color: "cyan" },
          ].map((c, i) => {
            const TI = c.icon;
            const colors = {
              blue:    "bg-blue-100 text-blue-700",
              violet:  "bg-violet-100 text-violet-700",
              emerald: "bg-emerald-100 text-emerald-700",
              cyan:    "bg-cyan-100 text-cyan-700",
            };
            return (
              <div key={c.label || `metric-${i}`} className="bg-white/70 backdrop-blur-sm border border-slate-200 rounded-xl p-3 flex flex-col items-center gap-1.5">
                <div className={`w-9 h-9 rounded-lg ${colors[c.color]} flex items-center justify-center`}>
                  <TI className="w-4 h-4" />
                </div>
                <div className="text-[10px] font-semibold text-slate-700 leading-tight text-center whitespace-pre-line">
                  {c.label}
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={onAddBuilding}
          className="mt-6 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-sm font-bold inline-flex items-center gap-2 hover:from-emerald-700 hover:to-emerald-800 shadow-md hover:shadow-lg transition-all"
          data-testid="immeuble-empty-add-btn"
        >
          <Plus className="w-5 h-5" /> Ajouter mon premier immeuble
        </button>
      </div>
    </div>
  );
}

// =====================================================================
//  STRUCTURE « DEUX BLOCS »
//
//  ┌─────────────────────────────────────────────────────────┐
//  │  TOP BLOCK — Zone Sélective (change selon la sélection) │
//  │  ┌─ rien    : DashboardGlobal                           │
//  │  ┌─ immeuble : Header + liste des apts                  │
//  │  └─ apt     : Header apt + Locataires + Journal tech    │
//  └─────────────────────────────────────────────────────────┘
//  ┌─────────────────────────────────────────────────────────┐
//  │  BOTTOM BLOCK — Bloc Fixe (TOUJOURS visible)            │
//  │  Grille de 7 tuiles d'actions globales                  │
//  └─────────────────────────────────────────────────────────┘
// =====================================================================

function TopBlock({
  selectedBuilding, apartments, selectedApt, onSelectApt, onClearApt,
  onEditBuilding, onEditApt,
  totalBuildings, totalApts, totalLeases, totalRevenue,
  onReload,
}) {
  // Cas 1 : Aucune sélection → Dashboard global
  if (!selectedBuilding) {
    return (
      <DashboardGlobal
        totalBuildings={totalBuildings}
        totalApts={totalApts}
        totalLeases={totalLeases}
        totalRevenue={totalRevenue}
      />
    );
  }

  // Cas 3 : Appartement sélectionné → infos appart (Locataires + Journal tech)
  if (selectedApt) {
    // Batch #14.191 — Quick toggle statut occupation (sans ouvrir modal édition)
    const onChangeOccupancy = async (newStatus) => {
      try {
        await axios.put(
          `${API}/portal/immeuble/units/${selectedApt.id}`,
          { occupancy_status: newStatus },
          { headers: authH() },
        );
        toast.success(
          newStatus == null
            ? "Statut occupation : Auto-détection"
            : `Statut occupation : ${newStatus === "occupied" ? "Occupé" : newStatus === "vacant" ? "Vacant" : "Transition"}`,
        );
        onReload?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur");
      }
    };
    return (
      <div className="space-y-3" data-testid="immeuble-top-block-apt">
        <ApartmentHeader
          apt={selectedApt}
          building={selectedBuilding}
          onClear={onClearApt}
          onEdit={onEditApt}
          onChangeOccupancy={onChangeOccupancy}
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ApartmentTenantsCard apt={selectedApt} onReload={onReload} />
          <ApartmentTicketsCard apt={selectedApt} onReload={onReload} />
        </div>
      </div>
    );
  }

  // Cas 2 : Immeuble seul sélectionné → header immeuble + liste compacte des apts
  return (
    <div className="space-y-3" data-testid="immeuble-top-block-building">
      <BuildingHeader building={selectedBuilding} onEdit={onEditBuilding} />
      <BuildingRentSummary building={selectedBuilding} apartments={apartments} />
      {/* Batch #14.231 — Loyers en retard (aging) + calculatrice TAL */}
      <RentArrearsBoard
        endpoint={`${API}/portal/immeuble/rent-arrears?building_id=${selectedBuilding.id}`}
        getHeaders={authH}
        testIdPrefix="portal-arrears"
      />
      <TalCalculator testIdPrefix="portal-tal" />
      <BuildingApartmentsList
        apartments={apartments}
        selectedAptId={null}
        onSelect={onSelectApt}
      />
    </div>
  );
}

function BottomFixedBlock({
  buildings, apartments, allApartments, selectedBuilding, selectedApt,
  onBuildingChange, onAptChange, onReload,
}) {
  const [activeAction, setActiveAction] = useState(null);

  // Batch #5 — Découplage Navigation/Actions : les modales d'action sont
  // 100% indépendantes du navigateur supérieur. Elles ont leur propre
  // sélecteur Immeuble/Appartement et ne modifient pas la sélection globale.
  // Le navigator sert UNIQUEMENT à afficher le contexte (infos) au-dessus.
  const noop = () => {};

  return (
    <div data-testid="immeuble-bottom-fixed-block">
      <BuildingActionsGrid onOpen={setActiveAction} />

      {/* Modal d'action — toujours montée ici (le Bloc Fixe est toujours visible) */}
      {activeAction === "stripe" ? (
        <StripeActionModal
          buildings={buildings}
          apartments={apartments}
          selectedBuilding={null}
          selectedApt={null}
          onBuildingChange={noop}
          onAptChange={noop}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "safe" || activeAction === "drop" ? (
        // Modale unifiée Documents — accepte les deux actionId pour rétro-compat
        <UnifiedDocumentDropModal
          buildings={buildings}
          apartments={apartments}
          selectedBuilding={null}
          selectedApt={null}
          onBuildingChange={noop}
          onAptChange={noop}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "rl31" ? (
        <Rl31ActionModal
          apartments={apartments}
          buildings={buildings}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "mileage" ? (
        <MileageActionModal
          buildings={buildings}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "credit_check" ? (
        <CreditCheckActionModal
          apartments={apartments}
          buildings={buildings}
          selectedBuilding={null}
          selectedApt={null}
          onBuildingChange={noop}
          onAptChange={noop}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "rent_increase" ? (
        <RentIncreaseActionModal
          apartments={apartments}
          buildings={buildings}
          selectedBuilding={null}
          selectedApt={null}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "journal" ? (
        <JournalActionModal
          apartments={allApartments}
          buildings={buildings}
          selectedBuilding={null}
          onClose={() => setActiveAction(null)}
          onReload={onReload}
        />
      ) : activeAction === "receipts" ? (
        <ReceiptsActionModal
          apartments={apartments}
          buildings={buildings}
          onClose={() => setActiveAction(null)}
        />
      ) : activeAction ? (
        <ActionPlaceholderModal
          actionId={activeAction}
          buildings={buildings}
          apartments={apartments}
          selectedBuilding={null}
          selectedApt={null}
          onBuildingChange={noop}
          onAptChange={noop}
          onClose={() => setActiveAction(null)}
        />
      ) : null}
    </div>
  );
}

// =====================================================================
//  Sous-composants utilisés par TopBlock
// =====================================================================
function BuildingHeader({ building, onEdit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="immeuble-building-header">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-slate-800 truncate">{building.name}</h2>
            <p className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{building.address}</span>
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge tone="blue">{building.units_count} apt</Badge>
              <Badge tone="emerald">{building.leases_active} bail{building.leases_active > 1 ? "x" : ""}</Badge>
              <Badge tone="amber">{building.monthly_revenue.toLocaleString("fr-CA")} $/mois</Badge>
            </div>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 flex-shrink-0"
          data-testid="immeuble-building-edit-btn"
        >
          <Pencil className="w-3.5 h-3.5" /> Modifier
        </button>
      </div>
    </div>
  );
}

// ---------- Récap loyers à l'échelle de l'immeuble (vue building seul) ----------
function BuildingRentSummary({ building, apartments }) {
  // Agrégation des paiements de l'année courante sur toutes les unités
  const currentYear = new Date().getFullYear();
  let totalUnitsRent = 0;
  let totalTenantsRent = 0;
  let paid = 0, late = 0, due = 0;
  let balanceDue = 0;

  apartments.forEach((a) => {
    const unitRent = a._raw?.monthly_rent ?? a.monthly_rent ?? 0;
    if (unitRent > 0) totalUnitsRent += unitRent;
    (a.unit_tenants || []).forEach((t) => {
      const eff = t.rent_amount ?? unitRent ?? 0;
      if (eff > 0) totalTenantsRent += eff;
    });
    (a.unit_rent_payments || []).forEach((p) => {
      if (p.period_year !== currentYear) return;
      if (p.status === "paid") paid += 1;
      else if (p.status === "late") { late += 1; balanceDue += Number(p.amount_due) || 0; }
      else { due += 1; balanceDue += Number(p.amount_due) || 0; }
    });
  });

  const hasAnyRent = totalUnitsRent > 0 || totalTenantsRent > 0;

  return (
    <div className="bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border border-emerald-100 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="immeuble-building-rent-summary">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-sm">
            <Banknote className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-sm">Récap loyers — {currentYear}</h3>
            <p className="text-[10px] text-slate-500">{building?.name}</p>
          </div>
        </div>
        {late > 0 && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {late} paiement{late > 1 ? "s" : ""} en retard
          </span>
        )}
      </div>

      {!hasAnyRent ? (
        <div className="text-center py-4 text-xs text-slate-500 italic">
          Aucun loyer défini sur les appartements ni les locataires.
          <br />
          <span className="text-[10px]">Sélectionnez un appartement et renseignez le loyer mensuel pour activer le suivi.</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <RentMiniKpi
            tone="emerald"
            icon={Building2}
            label="Loyer immeuble"
            value={`${totalUnitsRent.toLocaleString("fr-CA")} $`}
            sub="/ mois"
          />
          <RentMiniKpi
            tone="cyan"
            icon={Users}
            label="Loyer locataires"
            value={`${totalTenantsRent.toLocaleString("fr-CA")} $`}
            sub="/ mois (effectif)"
          />
          <RentMiniKpi
            tone="blue"
            icon={CheckCircle2}
            label="Payés / Année"
            value={`${paid}`}
            sub={`${due + late > 0 ? `${due + late} restant` : "tout est à jour"}`}
          />
          <RentMiniKpi
            tone={late > 0 ? "rose" : "amber"}
            icon={late > 0 ? AlertCircle : Clock}
            label={late > 0 ? "En retard" : "À payer"}
            value={`${balanceDue.toLocaleString("fr-CA")} $`}
            sub={late > 0 ? `${late} mois` : `${due} mois`}
          />
        </div>
      )}
    </div>
  );
}

function RentMiniKpi({ tone, icon: Icon, label, value, sub }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    cyan:    "bg-cyan-100 text-cyan-700 border-cyan-200",
    blue:    "bg-blue-100 text-blue-700 border-blue-200",
    amber:   "bg-amber-100 text-amber-700 border-amber-200",
    rose:    "bg-rose-100 text-rose-700 border-rose-200",
  };
  return (
    <div className="rounded-xl border bg-white/70 p-2.5 backdrop-blur-sm">
      <div className="flex items-center gap-1.5 mb-1">
        <div className={`w-6 h-6 rounded-md ${tones[tone]} flex items-center justify-center`}>
          <Icon className="w-3 h-3" />
        </div>
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      </div>
      <div className="text-base sm:text-lg font-bold text-slate-800 font-mono">{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}



// ---------- BLOC FIXE : grille de tuiles d'actions ----------
// Batch #14.150 — Toutes les tuiles ouvrent maintenant des PAGES (pas modals).
// `navigate` est l'URL cible — par défaut /portal/immeuble/action/{id}.
const BUILDING_ACTIONS = [
  { id: "safe",         label: "Documents",            icon: FolderOpen,    tone: "emerald", desc: "Coffre-fort + dépôt rapide unifiés" },
  { id: "lease",        label: "Bail TAL",             icon: FileSignature, tone: "blue",    desc: "Remplir + signer électroniquement", navigate: "/portal/bail-workflow" },
  { id: "stripe",       label: "Prélèvements Stripe", icon: Banknote,     tone: "violet",  desc: "Gestion bancaire" },
  { id: "mileage",      label: "Kilométrage",        icon: MapPin,        tone: "rose",    desc: "Trajets domicile-immeuble" },
  { id: "credit_check", label: "Enquête de crédit",  icon: ShieldCheck,   tone: "cyan",    desc: "Vérification du locataire" },
  { id: "rent_increase",label: "Renouvellements",     icon: TrendingUp,    tone: "indigo",  desc: "Hausse de loyer + lettre TAL" },
  { id: "rl31",         label: "Relevé 31 (RL-31)",   icon: Receipt,       tone: "amber",   desc: "Demande à l'équipe + saisie locataires" },
  { id: "journal",      label: "Conciliation manuelle", icon: BookOpen,      tone: "rose",    desc: "Loyers reçus hors-Stripe (Interac/Cash/Chèque)" },
  { id: "receipts",     label: "Quittances",          icon: ReceiptText,   tone: "emerald", desc: "Reçus de loyer émis automatiquement" },
  // Batch #14.191 — Suivi des travaux (centre complet entrepreneur + soumissions + photos + factures)
  { id: "work_orders",  label: "Suivi des travaux",  icon: Wrench,        tone: "amber",   desc: "Entrepreneur · soumission · photos · factures" },
];

const ACTION_TONES = {
  indigo:  { bg: "bg-indigo-50",  hover: "hover:bg-indigo-100",  border: "border-indigo-200",  iconBg: "bg-indigo-100",  iconText: "text-indigo-600",  ring: "hover:ring-indigo-300" },
  emerald: { bg: "bg-emerald-50", hover: "hover:bg-emerald-100", border: "border-emerald-200", iconBg: "bg-emerald-100", iconText: "text-emerald-600", ring: "hover:ring-emerald-300" },
  blue:    { bg: "bg-blue-50",    hover: "hover:bg-blue-100",    border: "border-blue-200",    iconBg: "bg-blue-100",    iconText: "text-blue-600",    ring: "hover:ring-blue-300" },
  violet:  { bg: "bg-violet-50",  hover: "hover:bg-violet-100",  border: "border-violet-200",  iconBg: "bg-violet-100",  iconText: "text-violet-600",  ring: "hover:ring-violet-300" },
  amber:   { bg: "bg-amber-50",   hover: "hover:bg-amber-100",   border: "border-amber-200",   iconBg: "bg-amber-100",   iconText: "text-amber-600",   ring: "hover:ring-amber-300" },
  rose:    { bg: "bg-rose-50",    hover: "hover:bg-rose-100",    border: "border-rose-200",    iconBg: "bg-rose-100",    iconText: "text-rose-600",    ring: "hover:ring-rose-300" },
  cyan:    { bg: "bg-cyan-50",    hover: "hover:bg-cyan-100",    border: "border-cyan-200",    iconBg: "bg-cyan-100",    iconText: "text-cyan-600",    ring: "hover:ring-cyan-300" },
};

function BuildingActionsGrid({ onOpen }) {
  // Batch #14.150 — Toutes les tuiles ouvrent une PAGE plein écran avec back arrow.
  // Tuile avec `navigate` (URL) → nav directe (ex: lease, rdv).
  // Sinon → /portal/immeuble/action/{id}
  const handle = (a) => {
    if (a.navigate) {
      window.location.href = a.navigate;
    } else {
      window.location.href = `/portal/immeuble/action/${a.id}`;
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="immeuble-actions-grid">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-indigo-500" />
        <h3 className="font-semibold text-slate-800 text-sm">Actions de l'immeuble</h3>
        <span className="text-[10px] text-slate-400 ml-1">— toujours accessibles</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {BUILDING_ACTIONS.map((a) => (
          <ActionTile key={a.id} action={a} onClick={() => handle(a)} />
        ))}
      </div>
    </div>
  );
}

function ActionTile({ action, onClick }) {
  const Icon = action.icon;
  const t = ACTION_TONES[action.tone] || ACTION_TONES.indigo;
  return (
    <button
      onClick={onClick}
      className={`group text-left ${t.bg} ${t.hover} border ${t.border} rounded-xl p-3 transition hover:shadow-sm hover:ring-2 ${t.ring} hover:ring-offset-1`}
      data-testid={`immeuble-action-tile-${action.id}`}
    >
      <div className={`w-9 h-9 rounded-lg ${t.iconBg} ${t.iconText} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-sm font-semibold text-slate-800 leading-tight">{action.label}</div>
      <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{action.desc}</div>
      <div className={`text-[10px] ${t.iconText} mt-1.5 opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1`}>
        Ouvrir <ArrowRight className="w-2.5 h-2.5" />
      </div>
    </button>
  );
}

// ---------- Liste compacte des appartements ----------
function BuildingApartmentsList({ apartments, selectedAptId, onSelect }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="immeuble-apts-list">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <DoorOpen className="w-4 h-4 text-blue-500" />
          <h3 className="font-semibold text-slate-800 text-sm">Appartements</h3>
          <span className="text-[11px] text-slate-400">({apartments.length})</span>
        </div>
      </div>
      {apartments.length === 0 ? (
        <EmptyHint icon={DoorOpen} message="Aucun appartement déclaré." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {apartments.map((a) => (
            <button
              key={a.id}
              onClick={() => onSelect(a.id)}
              className={`text-left rounded-xl p-3 transition border ${
                selectedAptId === a.id
                  ? "bg-blue-50 border-blue-400 ring-2 ring-blue-200"
                  : "bg-slate-50 border-slate-200 hover:bg-blue-50 hover:border-blue-300"
              }`}
              data-testid={`immeuble-apt-card-${a.id}`}
            >
              <div className="font-semibold text-slate-800 text-sm">Appartement {a.apt_number}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {[
                  a.bedrooms != null ? `${a.bedrooms} ch` : null,
                  a.monthly_rent > 0 ? `${a.monthly_rent.toLocaleString("fr-CA")} $/mois` : null,
                ].filter(Boolean).join(" · ") || <span className="italic text-slate-400">détails à compléter</span>}
              </div>
              <div className="flex gap-1 mt-1.5 flex-wrap">
                <OccupancyBadge apt={a} />
                <Badge tone="cyan">{a.active_tenants_count ?? a.tenants_count} loc.</Badge>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ApartmentHeader({ apt, building, onClear, onEdit, onChangeOccupancy }) {
  const [busyStatus, setBusyStatus] = useState(false);
  const setOccupancy = async (newStatus) => {
    setBusyStatus(true);
    try {
      await onChangeOccupancy?.(newStatus);
    } finally {
      setBusyStatus(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="immeuble-apt-header">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
            <DoorOpen className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-800">Appartement {apt.apt_number}</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{building?.name}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {apt.bedrooms != null && <Badge tone="blue">{apt.bedrooms} ch</Badge>}
              {apt.monthly_rent > 0 && <Badge tone="amber">{apt.monthly_rent.toLocaleString("fr-CA")} $/mois</Badge>}
              <Badge tone="cyan">{apt.active_tenants_count ?? apt.tenants_count} loc. actifs</Badge>
              <OccupancyBadge apt={apt} />
            </div>
            {/* Batch #14.191 — Toggle rapide statut occupation */}
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <span className="text-[10px] text-slate-500 uppercase font-bold mr-1">Forcer :</span>
              {[
                { v: null, label: "Auto" },
                { v: "vacant", label: "Vacant" },
                { v: "occupied", label: "Occupé" },
                { v: "transition", label: "Transition" },
              ].map((opt) => {
                const active = (apt.occupancy_status_manual || null) === opt.v;
                return (
                  <button
                    key={opt.v || "auto"}
                    onClick={() => !active && !busyStatus && setOccupancy(opt.v)}
                    disabled={busyStatus || active}
                    className={`text-[10px] px-2 py-0.5 rounded border transition ${
                      active
                        ? "bg-blue-600 text-white border-blue-700 cursor-default"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-blue-50"
                    } ${busyStatus ? "opacity-50" : ""}`}
                    data-testid={`apt-occupancy-quick-${opt.v || "auto"}-${apt.id}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {apt.occupancy_notes && (
              <div className="text-[11px] text-slate-500 italic mt-1.5">📝 {apt.occupancy_notes}</div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="immeuble-apt-edit-btn"
          >
            <Pencil className="w-3.5 h-3.5" /> Modifier
          </button>
          <button
            onClick={onClear}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="immeuble-apt-close-btn"
          >
            <X className="w-3.5 h-3.5" /> Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function ApartmentTenantsCard({ apt, onReload }) {
  const tenants = apt?.unit_tenants || [];
  const unitMonthlyRent = apt?._raw?.monthly_rent ?? apt?.monthly_rent ?? null;
  const allPayments = apt?.unit_rent_payments || [];
  const paymentsByTenant = {};
  allPayments.forEach((p) => {
    if (!paymentsByTenant[p.tenant_id]) paymentsByTenant[p.tenant_id] = [];
    paymentsByTenant[p.tenant_id].push(p);
  });

  const [modal, setModal] = useState(null); // null | { mode, target? }
  const [busyId, setBusyId] = useState(null);

  const onDelete = async (tenant) => {
    const fullname = `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim() || "ce locataire";
    if (!window.confirm(`Supprimer ${fullname} de cet appartement ?`)) return;
    setBusyId(tenant.id);
    try {
      await axios.delete(`${API}/portal/immeuble/tenants/${tenant.id}`, { headers: authH() });
      toast.success("Locataire supprimé");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid="immeuble-apt-tenants-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center">
            <Users className="w-4 h-4" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-800 text-sm">
              Locataires <span className="text-[10px] text-slate-400 font-normal">({tenants.length})</span>
            </h4>
            <p className="text-[10px] text-slate-500">
              Loyer appartement :{" "}
              {unitMonthlyRent != null && unitMonthlyRent > 0
                ? <b className="text-slate-700">{unitMonthlyRent.toLocaleString("fr-CA")} $/mois</b>
                : <span className="italic">non défini</span>}
            </p>
          </div>
        </div>
        <button
          onClick={() => setModal({ mode: "new" })}
          className="text-[11px] px-2.5 py-1 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 inline-flex items-center gap-1"
          data-testid="immeuble-apt-tenant-add"
        >
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </div>

      {tenants.length === 0 ? (
        <EmptyHint icon={Users} message="Aucun locataire pour cet appartement." />
      ) : (
        <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
          {/* Batch #14.226 — Éditeur de répartition du loyer (visible si 2+ loc.) */}
          {(apt?.active_leases || []).length > 0 && (apt.active_leases[0].tenant_ids || []).length > 1 && (
            <RentSplitEditor
              lease={apt.active_leases[0]}
              tenants={tenants}
              apiUrl={`${API}/portal/immeuble/leases/${apt.active_leases[0].id}/rent-split`}
              authHeaders={authH}
              onSaved={() => onReload?.()}
              testidPrefix="portal-rent-split"
            />
          )}
          {tenants.map((t) => {
            const effectiveRent = t.rent_amount ?? unitMonthlyRent ?? null;
            return (
              <div
                key={t.id}
                className="p-2.5 rounded-xl border border-slate-200 bg-slate-50/40 hover:border-cyan-200 transition"
                data-testid={`immeuble-apt-tenant-row-${t.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800 truncate">
                        {t.first_name} {t.last_name}
                      </span>
                      {effectiveRent != null && effectiveRent > 0 && (
                        <span className="text-[11px] font-mono px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                          {effectiveRent.toLocaleString("fr-CA")} $/mois
                        </span>
                      )}
                      {t.rent_amount != null && t.rent_amount !== unitMonthlyRent && (
                        <span className="text-[9px] uppercase tracking-wider text-cyan-600 font-semibold">override</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                      {t.email && <span>✉ {t.email}</span>}
                      {t.phone && <span>☎ {t.phone}</span>}
                    </div>
                    {t.notes && (
                      <div className="text-[10px] text-slate-400 italic mt-1 line-clamp-2">{t.notes}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      onClick={() => setModal({ mode: "edit", target: t })}
                      className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800"
                      title="Modifier"
                      data-testid={`immeuble-apt-tenant-edit-${t.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(t)}
                      disabled={busyId === t.id}
                      className="p-1 rounded hover:bg-rose-100 text-rose-500 hover:text-rose-700 disabled:opacity-40"
                      title="Supprimer"
                      data-testid={`immeuble-apt-tenant-delete-${t.id}`}
                    >
                      {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Étape 3.5 — Mini-dashboard paiements (grille 12 mois + stats) */}
                <RentPaymentsTracker
                  tenant={t}
                  payments={paymentsByTenant[t.id] || []}
                  effectiveRent={effectiveRent}
                  onReload={onReload}
                />
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <TenantFormModal
          mode={modal.mode}
          target={modal.target}
          unitId={apt.id}
          unitLabel={apt.apt_number}
          unitMonthlyRent={unitMonthlyRent}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            onReload?.();
          }}
        />
      )}
    </div>
  );
}

function ApartmentTicketsCard({ apt, onReload }) {
  const tickets = apt?.unit_tickets || [];
  const [modal, setModal] = useState(null); // null | { mode, target? }
  const [busyId, setBusyId] = useState(null);

  const byStatus = {
    open: tickets.filter((t) => t.status === "open"),
    in_progress: tickets.filter((t) => t.status === "in_progress"),
    done: tickets.filter((t) => t.status === "resolved" || t.status === "closed"),
  };

  const onDelete = async (ticket) => {
    if (!window.confirm(`Supprimer le ticket "${ticket.title}" ?`)) return;
    setBusyId(ticket.id);
    try {
      await axios.delete(`${API}/portal/immeuble/tickets/${ticket.id}`, { headers: authH() });
      toast.success("Ticket supprimé");
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid="immeuble-apt-tickets-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
            <Wrench className="w-4 h-4" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-800 text-sm">Journal technique <span className="text-[10px] text-slate-400 font-normal">({tickets.length})</span></h4>
            <p className="text-[10px] text-slate-500">Entretiens, réparations, anomalies</p>
          </div>
        </div>
        <button
          onClick={() => setModal({ mode: "new" })}
          className="text-[11px] px-2.5 py-1 rounded-lg bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-1"
          data-testid="immeuble-apt-ticket-add"
        >
          <Plus className="w-3 h-3" /> Ticket
        </button>
      </div>

      <div className="grid grid-cols-3 gap-1.5 mb-2">
        <TicketCountBadge title="À faire" tone="amber" icon={AlertCircle} count={byStatus.open.length} />
        <TicketCountBadge title="En cours" tone="blue" icon={Clock} count={byStatus.in_progress.length} />
        <TicketCountBadge title="Terminé" tone="emerald" icon={CheckCircle2} count={byStatus.done.length} />
      </div>

      {tickets.length === 0 ? (
        <EmptyHint icon={Wrench} message="Aucun ticket pour cet appartement." />
      ) : (
        <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
          {tickets.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              busy={busyId === t.id}
              onEdit={() => setModal({ mode: "edit", target: t })}
              onDelete={() => onDelete(t)}
            />
          ))}
        </div>
      )}

      {modal && (
        <TicketFormModal
          mode={modal.mode}
          target={modal.target}
          unitId={apt.id}
          unitLabel={apt.apt_number}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            onReload?.();
          }}
        />
      )}
    </div>
  );
}

function TicketCountBadge({ title, tone, icon: Icon, count }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50/40 text-amber-700",
    blue: "border-blue-200 bg-blue-50/40 text-blue-700",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
  };
  return (
    <div className={`border ${tones[tone]} rounded-xl p-2 text-center`}>
      <Icon className="w-3.5 h-3.5 mx-auto mb-0.5 opacity-70" />
      <div className="text-lg font-bold leading-none">{count}</div>
      <div className="text-[9px] uppercase tracking-wider opacity-80 mt-0.5">{title}</div>
    </div>
  );
}

const TICKET_STATUS_TONES = {
  open:        { label: "À faire",  cls: "bg-amber-100 text-amber-700",     dot: "bg-amber-500"   },
  in_progress: { label: "En cours", cls: "bg-blue-100 text-blue-700",       dot: "bg-blue-500"    },
  resolved:    { label: "Terminé",  cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  closed:      { label: "Fermé",    cls: "bg-slate-200 text-slate-700",     dot: "bg-slate-500"   },
};
const TICKET_PRIORITY_TONES = {
  low:    "bg-slate-100 text-slate-600",
  normal: "bg-blue-100 text-blue-700",
  high:   "bg-amber-100 text-amber-700",
  urgent: "bg-rose-100 text-rose-700",
};

function TicketRow({ ticket, busy, onEdit, onDelete }) {
  const stat = TICKET_STATUS_TONES[ticket.status] || TICKET_STATUS_TONES.open;
  return (
    <div
      className="flex items-start justify-between gap-2 p-2.5 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-orange-50/30 hover:border-orange-200 transition"
      data-testid={`immeuble-apt-ticket-row-${ticket.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          <span className={`inline-flex w-1.5 h-1.5 rounded-full ${stat.dot}`}></span>
          <span className="text-sm font-semibold text-slate-800 truncate">{ticket.title}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${stat.cls}`}>{stat.label}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${TICKET_PRIORITY_TONES[ticket.priority] || TICKET_PRIORITY_TONES.normal}`}>
            {ticket.priority}
          </span>
          {ticket.category && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{ticket.category}</span>
          )}
        </div>
        {ticket.description && (
          <div className="text-[10px] text-slate-500 italic mt-1 line-clamp-2">{ticket.description}</div>
        )}
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800"
          title="Modifier"
          data-testid={`immeuble-apt-ticket-edit-${ticket.id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          disabled={busy}
          className="p-1 rounded hover:bg-rose-100 text-rose-500 hover:text-rose-700 disabled:opacity-40"
          title="Supprimer"
          data-testid={`immeuble-apt-ticket-delete-${ticket.id}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
//  BARRE MOBILE — visible < lg uniquement
// =====================================================================
function MobileSelectorBar({
  buildings, apartments, selectedBuildingId, selectedAptId,
  onBuildingChange, onAptChange, onAddBuilding, onAddApt,
}) {
  return (
    <div className="lg:hidden mb-4 bg-white border border-slate-200 rounded-2xl p-3 shadow-sm space-y-2" data-testid="immeuble-mobile-selector-bar">
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <select
            value={selectedBuildingId || ""}
            onChange={(e) => onBuildingChange(e.target.value || null)}
            className="w-full appearance-none px-2.5 py-2 pr-7 border border-slate-300 rounded-lg bg-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
            data-testid="immeuble-mobile-select-building"
          >
            <option value="">— Tous mes immeubles —</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-2.5 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={selectedAptId || ""}
            onChange={(e) => onAptChange(e.target.value || null)}
            disabled={!selectedBuildingId}
            className="w-full appearance-none px-2.5 py-2 pr-7 border border-slate-300 rounded-lg bg-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer disabled:bg-slate-50"
            data-testid="immeuble-mobile-select-apt"
          >
            <option value="">— Apt —</option>
            {apartments.map((a) => (
              <option key={a.id} value={a.id}>Apt {a.unit_label || a.apt_number || "?"}</option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-2.5 text-slate-400 pointer-events-none" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onAddBuilding}
          className="px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-emerald-700"
          data-testid="immeuble-mobile-add-building"
        >
          <Plus className="w-3 h-3" /> Immeuble
        </button>
        <button
          onClick={onAddApt}
          disabled={!selectedBuildingId}
          className="px-2.5 py-1.5 rounded-lg bg-blue-600 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-blue-700 disabled:opacity-40"
          data-testid="immeuble-mobile-add-apt"
        >
          <Plus className="w-3 h-3" /> Appartement
        </button>
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = {
    blue: "bg-blue-100 text-blue-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    indigo: "bg-indigo-100 text-indigo-700",
    rose: "bg-rose-100 text-rose-700",
    cyan: "bg-cyan-100 text-cyan-700",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${tones[tone] || tones.indigo}`}>{children}</span>;
}

// =====================================================================
//  Composants utilitaires
// =====================================================================
const ACTION_DETAILS = {
  safe: {
    title: "Coffre-fort",
    subtitle: "Consulter les documents archivés de cet immeuble",
    tone: "indigo",
    icon: Lock,
    description: "Toutes les pièces importantes (titres de propriété, polices d'assurance, contrats, factures, RL-31 antérieurs, etc.) seront accessibles ici.",
    placeholders: ["Liste paginée des documents", "Filtres par catégorie", "Téléchargement direct"],
  },
  drop: {
    title: "Dépôt de document",
    subtitle: "Téléverser un nouveau document avec auto-classement",
    tone: "emerald",
    icon: Upload,
    description: "Drag & drop ou sélection. Champs optionnels pour catégorie (assurance, taxes, contrat, photo, etc.) et appartement lié (auto-classement).",
    placeholders: ["Zone drag & drop", "Catégorie (optionnel)", "Appartement lié (optionnel)", "Notes (optionnel)"],
  },
  lease: {
    title: "Générateur Bail TAL",
    subtitle: "Formulaire exhaustif — Sections A à I",
    tone: "blue",
    icon: FileSignature,
    description: "Formulaire complet du bail officiel TAL. Saisie 100 % manuelle (pas de pollution avec les fiches locataires existantes). PDF généré et copie auto envoyée à l'admin.",
    placeholders: [
      "A · Identification locataire et locateur",
      "B · Description du logement",
      "C · Durée du bail",
      "D · Loyer mensuel",
      "E · Services et conditions",
      "F · Restrictions",
      "G · Travaux à effectuer",
      "H · Avis au nouveau locataire",
      "I · Annexes et signatures",
    ],
  },
  stripe: {
    title: "Prélèvements Stripe",
    subtitle: "Coordonnées bancaires & historique des loyers perçus",
    tone: "violet",
    icon: Banknote,
    description: "Saisie manuelle d'après spécimen chèque. Historique brut des loyers perçus consultable par le client.",
    placeholders: ["N° de transit (5 chiffres)", "N° d'institution (3 chiffres)", "N° de compte (7-12 chiffres)", "Historique des prélèvements"],
  },
  rl31: {
    title: "Relevé 31 (RL-31)",
    subtitle: "Demande à l'équipe + saisie multi-locataires",
    tone: "amber",
    icon: Receipt,
    description: "Saisissez les infos de chaque locataire (nom, adresse, loyer payé, garderie, mois). Votre comptable accepte la demande, génère et transmet les RL-31 à Revenu Québec.",
    placeholders: ["Année fiscale (2024/2025/2026)", "Liste dynamique de locataires", "Loyer payé + service de garde par locataire"],
  },
  mileage: {
    title: "Suivi Kilométrique",
    subtitle: "Trajets domicile ↔ immeuble",
    tone: "rose",
    icon: MapPin,
    description: "Adresse de domicile demandée au premier usage, puis enregistrement rapide. Calcul automatique de la distance.",
    placeholders: ["Adresse de domicile (configuration initiale)", "Sens : Domicile → Immeuble / Immeuble → Domicile", "Date du trajet", "Motif (optionnel)"],
  },
  credit_check: {
    title: "Enquête de crédit",
    subtitle: "Vérification d'un candidat locataire",
    tone: "cyan",
    icon: ShieldCheck,
    description: "Lance une enquête de crédit pour un futur locataire avant signature du bail. Frais facturés selon le service utilisé.",
    placeholders: ["Identité du candidat (nom, NAS, date naissance)", "Consentement signé (upload)", "Choix du service (Equifax / TransUnion)", "Résultat et téléchargement du rapport"],
  },
  "edit-building": {
    title: "Modifier l'immeuble",
    subtitle: "Mise à jour des informations principales",
    tone: "indigo",
    icon: Pencil,
    description: "Tous les champs sont éditables. Les changements sont synchronisés avec le module opérationnel admin.",
    placeholders: ["Nom de l'immeuble", "Adresse complète", "Type (duplex/triplex/...)", "Année construction", "Prix d'achat", "Notes"],
  },
};

function ActionPlaceholderModal({
  actionId, onClose,
  buildings = [], apartments = [],
  selectedBuilding = null, selectedApt = null,
  onBuildingChange, onAptChange,
}) {
  const info = ACTION_DETAILS[actionId];
  if (!info) return null;
  const Icon = info.icon;
  const t = ACTION_TONES[info.tone] || ACTION_TONES.indigo;

  // Logique fallback : si aucun immeuble n'est sélectionné, on impose le choix
  // ici dans le modal avant de continuer.
  const needsBuildingSelector = !selectedBuilding;
  const needsAptSelector = selectedBuilding && !selectedApt && ACTIONS_REQUIRING_APT.includes(actionId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={`immeuble-action-modal-${actionId}`}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${t.iconBg} ${t.iconText} flex items-center justify-center`}>
              <Icon className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">{info.title}</h3>
              <p className="text-[10px] text-slate-500">{info.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {/* Context fallback — sélecteurs si aucun immeuble n'est choisi en amont */}
          {(needsBuildingSelector || needsAptSelector) && (
            <div className={`${t.bg} border ${t.border} rounded-xl p-3 space-y-2.5`} data-testid="immeuble-action-modal-context-selectors">
              <div className="text-[11px] font-semibold inline-flex items-center gap-1.5 text-slate-700">
                <AlertCircle className={`w-3.5 h-3.5 ${t.iconText}`} /> Veuillez d'abord choisir le contexte
              </div>
              {needsBuildingSelector && (
                <label className="block">
                  <span className="text-[10px] font-medium text-slate-600 inline-flex items-center gap-1 mb-1">
                    <Building2 className="w-3 h-3" /> Immeuble concerné <span className="text-rose-500">*</span>
                  </span>
                  <div className="relative">
                    <select
                      value=""
                      onChange={(e) => onBuildingChange?.(e.target.value || null)}
                      className="w-full appearance-none px-3 py-2 pr-8 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer"
                      data-testid="immeuble-action-modal-building-select"
                    >
                      <option value="" disabled>— Sélectionnez un immeuble —</option>
                      {buildings.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-2.5 top-2.5 text-slate-400 pointer-events-none" />
                  </div>
                </label>
              )}
              {!needsBuildingSelector && needsAptSelector && (
                <label className="block">
                  <span className="text-[10px] font-medium text-slate-600 inline-flex items-center gap-1 mb-1">
                    <DoorOpen className="w-3 h-3" /> Appartement concerné {ACTIONS_REQUIRING_APT.includes(actionId) && <span className="text-rose-500">*</span>}
                  </span>
                  <div className="relative">
                    <select
                      value=""
                      onChange={(e) => onAptChange?.(e.target.value || null)}
                      className="w-full appearance-none px-3 py-2 pr-8 border border-slate-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer"
                      data-testid="immeuble-action-modal-apt-select"
                    >
                      <option value="" disabled>— Sélectionnez un appartement —</option>
                      {apartments.map((a) => (
                        <option key={a.id} value={a.id}>Apt {a.unit_label || a.apt_number || "?"}{a.bedrooms != null ? ` · ${a.bedrooms} ch` : ""}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-2.5 top-2.5 text-slate-400 pointer-events-none" />
                  </div>
                </label>
              )}
              {selectedBuilding && (
                <div className="text-[10px] text-slate-500 bg-white/60 rounded p-2 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  Immeuble : <b className="text-slate-700">{selectedBuilding.name}</b>
                </div>
              )}
            </div>
          )}

          {/* Description de l'action */}
          <p className="text-xs text-slate-600 leading-relaxed">{info.description}</p>

          {/* Aperçu des champs du formulaire à venir */}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Éléments du formulaire</div>
            {info.placeholders.map((p, idx) => (
              <div key={`${info.title}-ph-${idx}-${p.slice(0, 20)}`} className="bg-slate-50 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-500 italic">
                {p}
              </div>
            ))}
          </div>

          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
            ⚙️ Skeleton — Cette fonctionnalité sera branchée sur les endpoints existants après validation visuelle.
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Fermer</button>
        </div>
      </div>
    </div>
  );
}

// Actions qui nécessitent un appartement spécifique (sinon → fallback sur l'immeuble entier)
const ACTIONS_REQUIRING_APT = ["credit_check"];


// =====================================================================
//  Modals placeholders
// =====================================================================
// =====================================================================
//  Modals — Création / Édition immeuble + appartement (Étape 2 wiring)
// =====================================================================

const PROPERTY_TYPES = ["immeuble_a_revenu", "duplex", "triplex", "quadruplex", "5plex", "6plex+", "condo", "maison", "commercial"];
const UNIT_TYPES = ["1.5", "2.5", "3.5", "4.5", "5.5", "6.5", "commercial", "stationnement", "rangement"];

function BuildingFormModal({ mode, target, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const t0 = target?._raw || target || {};
  const [form, setForm] = useState({
    name: t0.name || "",
    address_line: t0.address_line || "",
    city: t0.city || "",
    province: t0.province || "QC",
    postal_code: t0.postal_code || "",
    property_type: t0.property_type || "immeuble_a_revenu",
    year_built: t0.year_built ?? "",
    nb_units_total: t0.nb_units_total ?? 1,
    notes: t0.notes || "",
    // Batch #10.1 — Propriétaires légaux (1 ou 2 copropriétaires)
    // Batch #14.209 — _uid stable pour clé React (reconciliation correcte add/remove)
    legal_owners: Array.isArray(t0.legal_owners) && t0.legal_owners.length > 0
      ? t0.legal_owners.map((o, i) => ({
          _uid: o._uid || `owner-${Date.now()}-${i}`,
          first_name: o.first_name || "",
          last_name: o.last_name || "",
          legal_name: o.legal_name || "",
          address_line: o.address_line || "",
          city: o.city || "",
          province: o.province || "QC",
          postal_code: o.postal_code || "",
          phone: o.phone || "",
          email: o.email || "",
        }))
      : [{ _uid: `owner-${Date.now()}-0`, first_name: "", last_name: "", legal_name: "", address_line: "",
           city: t0.city || "", province: "QC", postal_code: t0.postal_code || "",
           phone: "", email: "" }],
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updOwner = (idx, key, value) => setForm((f) => ({
    ...f,
    legal_owners: f.legal_owners.map((o, i) => i === idx ? { ...o, [key]: value } : o),
  }));
  const addOwner = () => setForm((f) => ({
    ...f,
    legal_owners: f.legal_owners.length >= 2 ? f.legal_owners : [
      ...f.legal_owners,
      { _uid: `owner-${Date.now()}-${f.legal_owners.length}`, first_name: "", last_name: "", legal_name: "", address_line: "",
        city: f.city || "", province: "QC", postal_code: f.postal_code || "",
        phone: "", email: "" },
    ],
  }));
  const removeOwner = (idx) => setForm((f) => ({
    ...f,
    legal_owners: f.legal_owners.filter((_, i) => i !== idx),
  }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Le nom de l'immeuble est requis"); return; }
    if (!form.address_line.trim()) { toast.error("L'adresse est requise"); return; }
    if (!form.city.trim()) { toast.error("La ville est requise"); return; }
    if (!form.postal_code.trim()) { toast.error("Le code postal est requis"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        address_line: form.address_line.trim(),
        city: form.city.trim(),
        province: form.province || "QC",
        postal_code: form.postal_code.trim(),
        property_type: form.property_type || "immeuble_a_revenu",
        nb_units_total: Number(form.nb_units_total) || 1,
        year_built: form.year_built === "" ? null : Number(form.year_built),
        notes: form.notes || null,
        // Batch #10.1 — Filtre les owners vides (au moins nom ou prénom requis)
        legal_owners: (form.legal_owners || [])
          .filter((o) => (o.first_name || "").trim() || (o.last_name || "").trim() || (o.legal_name || "").trim())
          .map((o) => ({
            first_name: (o.first_name || "").trim() || null,
            last_name: (o.last_name || "").trim() || null,
            legal_name: (o.legal_name || "").trim() || null,
            address_line: (o.address_line || "").trim() || null,
            city: (o.city || "").trim() || null,
            province: (o.province || "QC").trim(),
            postal_code: (o.postal_code || "").trim() || null,
            phone: (o.phone || "").trim() || null,
            email: (o.email || "").trim() || null,
          })),
        // client_id requis par Pydantic mais écrasé par le backend depuis le contexte
        client_id: "_portal",
      };
      const url = isEdit
        ? `${API}/portal/immeuble/buildings/${target.id}`
        : `${API}/portal/immeuble/buildings`;
      if (isEdit) {
        await axios.put(url, payload, { headers: authH() });
      } else {
        await axios.post(url, payload, { headers: authH() });
      }
      toast.success(isEdit ? "Immeuble mis à jour" : "Immeuble créé");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((x) => `${x.loc?.slice(1).join(".")} : ${x.msg}`).join(" · ") :
        "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-building-form-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
              {isEdit ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                {isEdit ? "Modifier l'immeuble" : "Nouvel immeuble"}
              </h3>
              <p className="text-[10px] text-slate-500">
                {isEdit ? "Mise à jour des informations" : "Ajout à votre parc immobilier"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          <FieldLabel label="Nom de l'immeuble" required>
            <input
              value={form.name}
              onChange={(e) => upd("name", e.target.value)}
              maxLength={120}
              placeholder="ex: Plex Saint-Denis"
              className="cc-input"
              data-testid="immeuble-form-name"
            />
          </FieldLabel>

          <FieldLabel label="Adresse complète" required>
            <input
              value={form.address_line}
              onChange={(e) => upd("address_line", e.target.value)}
              maxLength={200}
              placeholder="ex: 4321 rue Saint-Denis"
              className="cc-input"
              data-testid="immeuble-form-address"
            />
          </FieldLabel>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FieldLabel label="Ville" required>
              <input value={form.city} onChange={(e) => upd("city", e.target.value)} maxLength={80} className="cc-input" data-testid="immeuble-form-city" />
            </FieldLabel>
            <FieldLabel label="Province">
              <input value={form.province} onChange={(e) => upd("province", e.target.value)} maxLength={4} className="cc-input font-mono" data-testid="immeuble-form-province" />
            </FieldLabel>
            <FieldLabel label="Code postal" required>
              <input value={form.postal_code} onChange={(e) => upd("postal_code", e.target.value)} maxLength={12} placeholder="H2J 2L1" className="cc-input font-mono" data-testid="immeuble-form-postal" />
            </FieldLabel>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FieldLabel label="Type">
              <select value={form.property_type} onChange={(e) => upd("property_type", e.target.value)} className="cc-input" data-testid="immeuble-form-type">
                {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Année construction">
              <input type="number" value={form.year_built} onChange={(e) => upd("year_built", e.target.value)} min="1800" max="2100" className="cc-input font-mono" data-testid="immeuble-form-year" />
            </FieldLabel>
            <FieldLabel label="Nb. logements total" required>
              <input type="number" value={form.nb_units_total} onChange={(e) => upd("nb_units_total", e.target.value)} min="1" max="500" className="cc-input font-mono" data-testid="immeuble-form-units-total" />
            </FieldLabel>
          </div>

          <FieldLabel label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} maxLength={1000} className="cc-input" data-testid="immeuble-form-notes" />
          </FieldLabel>

          {/* Batch #10.1 — Propriétaires légaux (apparaissent en Section A du bail TAL) */}
          <div className="mt-2 pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-semibold text-slate-800 inline-flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-emerald-600" />
                  Propriétaires légaux
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Apparaissent en Section A (Locateur) du bail TAL · 1 ou 2 copropriétaires
                </p>
              </div>
              {form.legal_owners.length < 2 && (
                <button
                  type="button"
                  onClick={addOwner}
                  className="text-[11px] px-2 py-1 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1"
                  data-testid="immeuble-form-add-owner"
                >
                  <Plus className="w-3 h-3" /> Ajouter un copropriétaire
                </button>
              )}
            </div>
            <div className="space-y-3">
              {form.legal_owners.map((owner, idx) => (
                <div key={owner._uid || `owner-fallback-${idx}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50/60"
                     data-testid={`immeuble-form-owner-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-slate-700">
                      Propriétaire {idx + 1}{idx === 0 ? " (principal)" : ""}
                    </div>
                    {form.legal_owners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeOwner(idx)}
                        className="text-[10px] text-rose-600 hover:text-rose-800 inline-flex items-center gap-0.5"
                        data-testid={`immeuble-form-owner-${idx}-remove`}
                      >
                        <Trash2 className="w-3 h-3" /> Retirer
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <FieldLabel label="Prénom">
                      <input
                        value={owner.first_name}
                        onChange={(e) => updOwner(idx, "first_name", e.target.value)}
                        maxLength={80}
                        className="cc-input"
                        data-testid={`immeuble-form-owner-${idx}-first-name`}
                      />
                    </FieldLabel>
                    <FieldLabel label="Nom">
                      <input
                        value={owner.last_name}
                        onChange={(e) => updOwner(idx, "last_name", e.target.value)}
                        maxLength={80}
                        className="cc-input"
                        data-testid={`immeuble-form-owner-${idx}-last-name`}
                      />
                    </FieldLabel>
                  </div>
                  <FieldLabel label="OU Raison sociale (personne morale)">
                    <input
                      value={owner.legal_name}
                      onChange={(e) => updOwner(idx, "legal_name", e.target.value)}
                      maxLength={200}
                      placeholder="ex: Société Acme Immeubles"
                      className="cc-input"
                      data-testid={`immeuble-form-owner-${idx}-legal-name`}
                    />
                  </FieldLabel>
                  <FieldLabel label="Adresse complète">
                    <input
                      value={owner.address_line}
                      onChange={(e) => updOwner(idx, "address_line", e.target.value)}
                      maxLength={200}
                      placeholder="ex: 8550 J.-R.-Théberge"
                      className="cc-input"
                      data-testid={`immeuble-form-owner-${idx}-address`}
                    />
                  </FieldLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <FieldLabel label="Ville">
                      <input value={owner.city} onChange={(e) => updOwner(idx, "city", e.target.value)} maxLength={80} className="cc-input" data-testid={`immeuble-form-owner-${idx}-city`} />
                    </FieldLabel>
                    <FieldLabel label="Province">
                      <input value={owner.province} onChange={(e) => updOwner(idx, "province", e.target.value)} maxLength={4} className="cc-input font-mono" />
                    </FieldLabel>
                    <FieldLabel label="Code postal">
                      <input value={owner.postal_code} onChange={(e) => updOwner(idx, "postal_code", e.target.value)} maxLength={12} className="cc-input font-mono" data-testid={`immeuble-form-owner-${idx}-postal`} />
                    </FieldLabel>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <FieldLabel label="Téléphone">
                      <input
                        value={owner.phone}
                        onChange={(e) => updOwner(idx, "phone", e.target.value)}
                        maxLength={30}
                        placeholder="ex: 418 555-1234"
                        className="cc-input font-mono"
                        data-testid={`immeuble-form-owner-${idx}-phone`}
                      />
                    </FieldLabel>
                    <FieldLabel label="Courriel">
                      <input
                        type="email"
                        value={owner.email}
                        onChange={(e) => updOwner(idx, "email", e.target.value)}
                        maxLength={120}
                        className="cc-input"
                        data-testid={`immeuble-form-owner-${idx}-email`}
                      />
                    </FieldLabel>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-form-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Créer l'immeuble"}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}

function UnitFormModal({ mode, target, buildingId, buildings, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const t0 = target?._raw || target || {};
  const [form, setForm] = useState({
    building_id: buildingId || t0.property_id || (buildings[0]?.id || ""),
    unit_label: t0.unit_label || "",
    unit_type: t0.unit_type || "4.5",
    nb_rooms: t0.nb_rooms ?? "",
    surface_sqft: t0.surface_sqft ?? "",
    monthly_rent: t0.monthly_rent ?? "",
    has_balcony: !!t0.has_balcony,
    has_parking: !!t0.has_parking,
    has_storage: !!t0.has_storage,
    notes: t0.notes || "",
    // Batch #14.191 — Statut occupation manuel
    occupancy_status: t0.occupancy_status || "",  // "" = laisser en auto
    occupancy_notes: t0.occupancy_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Si pas d'immeuble en contexte (création depuis un état vide) on impose le choix
  const needsBuildingPick = !isEdit && !buildingId && buildings.length > 0;

  const submit = async () => {
    if (!form.unit_label.trim()) {
      toast.error("Le numéro d'unité est requis");
      return;
    }
    if (!form.building_id) {
      toast.error("Sélectionnez l'immeuble auquel rattacher cet appartement");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        unit_label: form.unit_label.trim(),
        unit_type: form.unit_type || "4.5",
        nb_rooms: form.nb_rooms === "" ? null : Number(form.nb_rooms),
        surface_sqft: form.surface_sqft === "" ? null : Number(form.surface_sqft),
        monthly_rent: form.monthly_rent === "" ? null : Number(form.monthly_rent),
        has_balcony: !!form.has_balcony,
        has_parking: !!form.has_parking,
        has_storage: !!form.has_storage,
        notes: form.notes || null,
        // Batch #14.191
        occupancy_status: form.occupancy_status || null,  // null = laisser en auto-détection
        occupancy_notes: form.occupancy_notes || null,
        // Champs requis par UnitCreate (écrasés/forcés au backend)
        property_id: form.building_id,
      };
      if (isEdit) {
        await axios.put(`${API}/portal/immeuble/units/${target.id}`, payload, { headers: authH() });
      } else {
        await axios.post(`${API}/portal/immeuble/buildings/${form.building_id}/units`, payload, { headers: authH() });
      }
      toast.success(isEdit ? "Appartement mis à jour" : "Appartement ajouté");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((x) => `${x.loc?.slice(1).join(".")} : ${x.msg}`).join(" · ") :
        "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-unit-form-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
              {isEdit ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                {isEdit ? "Modifier l'appartement" : "Nouvel appartement"}
              </h3>
              <p className="text-[10px] text-slate-500">
                {isEdit ? "Mise à jour de l'unité" : "Ajout dans un immeuble"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {needsBuildingPick && (
            <FieldLabel label="Immeuble" required>
              <select value={form.building_id} onChange={(e) => upd("building_id", e.target.value)} className="cc-input" data-testid="immeuble-unit-form-building">
                <option value="" disabled>— Sélectionnez un immeuble —</option>
                {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </FieldLabel>
          )}
          {isEdit && (
            <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              Cet appartement appartient à l'immeuble&nbsp;:&nbsp;
              <b>{buildings.find((b) => b.id === t0.property_id)?.name || "—"}</b>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldLabel label="N° d'appartement" required>
              <input value={form.unit_label} onChange={(e) => upd("unit_label", e.target.value)} maxLength={30} placeholder="ex: 1, 2A, RDC…" className="cc-input" data-testid="immeuble-unit-form-label" />
            </FieldLabel>
            <FieldLabel label="Type">
              <select value={form.unit_type} onChange={(e) => upd("unit_type", e.target.value)} className="cc-input" data-testid="immeuble-unit-form-type">
                {UNIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FieldLabel>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldLabel label="Nombre de pièces (nb_rooms)">
              <input type="number" step="0.5" value={form.nb_rooms} onChange={(e) => upd("nb_rooms", e.target.value)} min="0" max="20" className="cc-input font-mono" data-testid="immeuble-unit-form-rooms" />
            </FieldLabel>
            <FieldLabel label="Surface (pi²)">
              <input type="number" value={form.surface_sqft} onChange={(e) => upd("surface_sqft", e.target.value)} min="0" className="cc-input font-mono" data-testid="immeuble-unit-form-surface" />
            </FieldLabel>
          </div>

          <FieldLabel label="Loyer mensuel attendu ($/mois)">
            <div className="relative">
              <input type="number" step="0.01" min="0" value={form.monthly_rent} onChange={(e) => upd("monthly_rent", e.target.value)} placeholder="ex: 950" className="cc-input font-mono pr-10" data-testid="immeuble-unit-form-rent" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
            </div>
          </FieldLabel>

          <div className="grid grid-cols-3 gap-2">
            <label className="flex items-center gap-2 text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={form.has_balcony} onChange={(e) => upd("has_balcony", e.target.checked)} data-testid="immeuble-unit-form-balcony" />
              Balcon
            </label>
            <label className="flex items-center gap-2 text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={form.has_parking} onChange={(e) => upd("has_parking", e.target.checked)} data-testid="immeuble-unit-form-parking" />
              Stationnement
            </label>
            <label className="flex items-center gap-2 text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={form.has_storage} onChange={(e) => upd("has_storage", e.target.checked)} data-testid="immeuble-unit-form-storage" />
              Rangement
            </label>
          </div>

          <FieldLabel label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" data-testid="immeuble-unit-form-notes" />
          </FieldLabel>

          {/* Batch #14.191 — Statut d'occupation manuel (3 états) */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Statut d'occupation</div>
            <p className="text-[10px] text-slate-500">
              Source unique pour ce logement. Laissez « Automatique » pour calculer d'après les locataires actifs, ou forcez manuellement.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {[
                { v: "", label: "🔄 Auto", desc: "Détecté" },
                { v: "vacant", label: "🔴 Vacant", desc: "Libre" },
                { v: "occupied", label: "🟢 Occupé", desc: "Loué" },
                { v: "transition", label: "🟡 Transition", desc: "Entre 2" },
              ].map((opt) => (
                <button
                  key={opt.v || "auto"}
                  type="button"
                  onClick={() => upd("occupancy_status", opt.v)}
                  className={`px-2 py-1.5 text-[11px] rounded border transition ${
                    form.occupancy_status === opt.v
                      ? "bg-blue-600 text-white border-blue-700 font-bold"
                      : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                  }`}
                  data-testid={`immeuble-unit-form-occupancy-${opt.v || "auto"}`}
                >
                  <div>{opt.label}</div>
                  <div className="text-[9px] opacity-70">{opt.desc}</div>
                </button>
              ))}
            </div>
            <input
              type="text"
              value={form.occupancy_notes}
              onChange={(e) => upd("occupancy_notes", e.target.value)}
              placeholder="Ex: À louer dès le 1er juillet 2026"
              className="cc-input text-xs"
              data-testid="immeuble-unit-form-occupancy-notes"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-unit-form-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Ajouter"}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1 mb-1">
        {label}{required && <span className="text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );
}

// ModalStyles extracted to ./modals/shared.jsx (Batch #12 refactor)

// =====================================================================
//  Modal placeholder (générique, gardé pour les boutons sans form encore branché)
// =====================================================================
function PlaceholderModal({ title, subtitle, tone, fields, onClose }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-600",
    blue: "bg-blue-100 text-blue-600",
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-placeholder-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg ${tones[tone]} flex items-center justify-center`}>
              <Plus className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
              {subtitle && <p className="text-[10px] text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-2.5">
          {fields.map((f) => (
            <div key={f} className="bg-slate-50 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-500 italic">
              {f}
            </div>
          ))}
          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
            ⚙️ Formulaire à brancher après validation visuelle du skeleton.
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Fermer</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
//  Modals — Locataires & Tickets (Étape 3 wiring)
// =====================================================================
function TenantFormModal({ mode, target, unitId, unitLabel, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const t0 = target || {};
  const [form, setForm] = useState({
    first_name: t0.first_name || "",
    last_name: t0.last_name || "",
    email: t0.email || "",
    phone: t0.phone || "",
    previous_address: t0.previous_address || "",
    emergency_contact_name: t0.emergency_contact_name || "",
    emergency_contact_phone: t0.emergency_contact_phone || "",
    rent_amount: t0.rent_amount ?? "",
    notes: t0.notes || "",
    is_active: t0.is_active !== false,  // Batch #14.182 — défaut TRUE
    notify_by_sms: t0.notify_by_sms === true,  // Batch #14.188 — défaut FALSE (opt-in explicite)
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.first_name.trim()) { toast.error("Le prénom est requis"); return; }
    if (!form.last_name.trim()) { toast.error("Le nom est requis"); return; }
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        previous_address: form.previous_address.trim() || null,
        emergency_contact_name: form.emergency_contact_name.trim() || null,
        emergency_contact_phone: form.emergency_contact_phone.trim() || null,
        rent_amount: form.rent_amount === "" ? null : Number(form.rent_amount),
        notes: form.notes.trim() || null,
        is_active: !!form.is_active,  // Batch #14.182
        notify_by_sms: !!form.notify_by_sms,  // Batch #14.188
      };
      if (isEdit) {
        await axios.put(`${API}/portal/immeuble/tenants/${target.id}`, payload, { headers: authH() });
      } else {
        await axios.post(`${API}/portal/immeuble/units/${unitId}/tenants`, payload, { headers: authH() });
      }
      toast.success(isEdit ? "Locataire mis à jour" : "Locataire ajouté");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((x) => `${x.loc?.slice(1).join(".")} : ${x.msg}`).join(" · ") :
        "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-tenant-form-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center">
              {isEdit ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                {isEdit ? "Modifier le locataire" : "Nouveau locataire"}
              </h3>
              <p className="text-[10px] text-slate-500">
                Appartement {unitLabel || "—"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldLabel label="Prénom" required>
              <input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} maxLength={60} className="cc-input" data-testid="immeuble-tenant-form-first-name" />
            </FieldLabel>
            <FieldLabel label="Nom" required>
              <input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} maxLength={60} className="cc-input" data-testid="immeuble-tenant-form-last-name" />
            </FieldLabel>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldLabel label="Courriel">
              <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} maxLength={120} placeholder="ex: marie@exemple.ca" className="cc-input" data-testid="immeuble-tenant-form-email" />
            </FieldLabel>
            <FieldLabel label="Téléphone">
              <input value={form.phone} onChange={(e) => upd("phone", e.target.value)} maxLength={30} placeholder="514-555-1234" className="cc-input font-mono" data-testid="immeuble-tenant-form-phone" />
            </FieldLabel>
          </div>

          {/* Batch #14.188 — Opt-in SMS pour ce locataire */}
          <label
            className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition ${
              form.phone.trim()
                ? "bg-cyan-50 border-cyan-300 hover:bg-cyan-100"
                : "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed"
            }`}
            data-testid="immeuble-tenant-form-notify-sms-label"
            title={form.phone.trim() ? "" : "Renseignez d'abord un numéro de téléphone"}
          >
            <input
              type="checkbox"
              checked={!!form.notify_by_sms}
              disabled={!form.phone.trim()}
              onChange={(e) => upd("notify_by_sms", e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-cyan-600"
              data-testid="immeuble-tenant-form-notify-sms"
            />
            <div className="flex-1">
              <div className="text-xs font-semibold text-cyan-900 flex items-center gap-1.5">
                <Smartphone className="w-3.5 h-3.5" /> Activer les notifications SMS
              </div>
              <div className="text-[10px] text-cyan-700 mt-0.5 leading-snug">
                Le locataire recevra par texto : avis de renouvellement, relances de signature, quittances et rappels (selon les canaux activés côté cabinet). Désactivable à tout moment.
              </div>
            </div>
          </label>

          <FieldLabel label="Adresse précédente (optionnel)">
            <input value={form.previous_address} onChange={(e) => upd("previous_address", e.target.value)} maxLength={240} className="cc-input" data-testid="immeuble-tenant-form-prev-address" />
          </FieldLabel>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldLabel label="Contact d'urgence (nom)">
              <input value={form.emergency_contact_name} onChange={(e) => upd("emergency_contact_name", e.target.value)} maxLength={120} className="cc-input" data-testid="immeuble-tenant-form-emergency-name" />
            </FieldLabel>
            <FieldLabel label="Contact d'urgence (tél)">
              <input value={form.emergency_contact_phone} onChange={(e) => upd("emergency_contact_phone", e.target.value)} maxLength={30} className="cc-input font-mono" data-testid="immeuble-tenant-form-emergency-phone" />
            </FieldLabel>
          </div>

          <FieldLabel label="Loyer ($/mois) — override individuel (optionnel)">
            <div className="relative">
              <input type="number" step="0.01" min="0" value={form.rent_amount} onChange={(e) => upd("rent_amount", e.target.value)} placeholder="Laisser vide pour utiliser le loyer de l'appartement" className="cc-input font-mono pr-10" data-testid="immeuble-tenant-form-rent" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
            </div>
          </FieldLabel>

          <FieldLabel label="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" data-testid="immeuble-tenant-form-notes" />
          </FieldLabel>

          {/* Batch #14.182 — Statut actif (filet de sécurité pour la vue admin) */}
          <label className="flex items-center gap-2 p-2.5 rounded-lg bg-cyan-50 border border-cyan-200 cursor-pointer hover:bg-cyan-100 transition" data-testid="immeuble-tenant-form-active-label">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => upd("is_active", e.target.checked)}
              className="w-4 h-4 accent-cyan-600"
              data-testid="immeuble-tenant-form-active"
            />
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-800">Locataire actif</div>
              <div className="text-[10px] text-slate-500">
                Décochez pour marquer ce locataire comme inactif (sans le supprimer). Un logement sans locataire actif apparaît comme « vacant » côté gestion.
              </div>
            </div>
          </label>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-tenant-form-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Ajouter le locataire"}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}
// =====================================================================
//  Mini-dashboard paiements de loyer — grille 12 mois + stats (Étape 3.5)
//  Cycle 3 états sur le carré : due (slate) → paid (emerald) → late (rose) → due
// =====================================================================
const MONTH_LABELS = ["J","F","M","A","M","J","J","A","S","O","N","D"];
const MONTH_FULL = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const RENT_PAYMENT_TONES = {
  due:  { bg: "bg-slate-200 hover:bg-slate-300",       text: "text-slate-600", ring: "ring-slate-300",   label: "À payer" },
  paid: { bg: "bg-emerald-500 hover:bg-emerald-600",   text: "text-white",     ring: "ring-emerald-400", label: "Payé" },
  late: { bg: "bg-rose-500 hover:bg-rose-600",         text: "text-white",     ring: "ring-rose-400",    label: "En retard" },
};
const NEXT_STATUS = { due: "paid", paid: "late", late: "due" };

function RentPaymentsTracker({ tenant, payments, effectiveRent, onReload }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [busyMonth, setBusyMonth] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Map des paiements par mois (1..12) pour l'année courante
  const paymentsForYear = payments.filter((p) => p.period_year === year);
  const byMonth = {};
  paymentsForYear.forEach((p) => { byMonth[p.period_month] = p; });

  const counts = {
    paid: paymentsForYear.filter((p) => p.status === "paid").length,
    late: paymentsForYear.filter((p) => p.status === "late").length,
    due:  paymentsForYear.filter((p) => p.status === "due").length,
  };
  const hasAny = paymentsForYear.length > 0;
  const balanceDue = paymentsForYear
    .filter((p) => p.status !== "paid")
    .reduce((s, p) => s + (Number(p.amount_due) || 0), 0);

  const cycleStatus = async (month) => {
    const current = byMonth[month];
    if (!current) {
      // Pas de paiement existant pour ce mois → créer en "paid" directement
      setBusyMonth(month);
      try {
        await axios.post(`${API}/portal/immeuble/rent-payments`, {
          tenant_id: tenant.id,
          period_year: year,
          period_month: month,
          amount_due: effectiveRent ?? 0,
          status: "paid",
        }, { headers: authH() });
        toast.success(`${MONTH_FULL[month - 1]} ${year} marqué payé`);
        onReload?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Erreur lors de la création");
      } finally {
        setBusyMonth(null);
      }
      return;
    }
    setBusyMonth(month);
    try {
      const nextStatus = NEXT_STATUS[current.status] || "due";
      await axios.put(`${API}/portal/immeuble/rent-payments/${current.id}`, { status: nextStatus }, { headers: authH() });
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setBusyMonth(null);
    }
  };

  const generateCalendar = async () => {
    if (effectiveRent == null || effectiveRent <= 0) {
      const ok = window.confirm("Aucun montant de loyer défini sur ce locataire ni sur l'appartement. Générer quand même avec 0 $ par mois ?");
      if (!ok) return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/portal/immeuble/rent-payments/generate`, {
        tenant_id: tenant.id,
        year,
      }, { headers: authH() });
      toast.success(`Calendrier ${year} généré : ${r.data.created} créé(s), ${r.data.skipped} préservé(s)`);
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="mt-2.5 pt-2.5 border-t border-slate-200" data-testid={`immeuble-rent-tracker-${tenant.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="inline-flex items-center gap-1.5">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white font-mono cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyan-300"
            data-testid={`immeuble-rent-year-${tenant.id}`}
          >
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
              <b className="text-emerald-700">{counts.paid}</b>
              <span className="text-slate-500">payé{counts.paid > 1 ? "s" : ""}</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
              <b className="text-rose-700">{counts.late}</b>
              <span className="text-slate-500">retard</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
              <b className="text-slate-700">{counts.due}</b>
              <span className="text-slate-500">à payer</span>
            </span>
          </div>
        </div>
        <button
          onClick={generateCalendar}
          disabled={generating}
          className="text-[10px] px-2 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 disabled:opacity-50 inline-flex items-center gap-1"
          title={`Générer les 12 mois ${year} si manquants`}
          data-testid={`immeuble-rent-generate-${tenant.id}`}
        >
          {generating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Calendar className="w-2.5 h-2.5" />}
          {hasAny ? `Compléter ${year}` : `Générer ${year}`}
        </button>
      </div>

      {/* Grille 12 mois — boutons compacts, optimisés tactile (min 32x32) */}
      <div className="grid grid-cols-12 gap-1">
        {MONTH_LABELS.map((lbl, idx) => {
          const month = idx + 1;
          const p = byMonth[month];
          const status = p?.status || "due";
          const tone = RENT_PAYMENT_TONES[status];
          const isBusy = busyMonth === month;
          const tip = p
            ? `${MONTH_FULL[idx]} ${year} · ${tone.label}${p.amount_due ? ` · ${Number(p.amount_due).toLocaleString("fr-CA")} $` : ""}`
            : `${MONTH_FULL[idx]} ${year} · (cliquer pour marquer payé)`;
          return (
            <button
              key={month}
              type="button"
              onClick={() => cycleStatus(month)}
              disabled={isBusy}
              title={tip}
              className={`
                relative aspect-square min-w-[28px] min-h-[28px] rounded-md
                ${p ? tone.bg : "bg-slate-100 hover:bg-slate-200"}
                ${p ? tone.text : "text-slate-400"}
                text-[11px] font-bold font-mono
                transition-all duration-200 ease-out
                hover:scale-110 active:scale-95
                focus:outline-none focus:ring-2 ${tone.ring} focus:ring-offset-1
                disabled:opacity-50 disabled:cursor-wait
                ${!p ? "border border-dashed border-slate-300" : "shadow-sm"}
              `}
              data-testid={`immeuble-rent-cell-${tenant.id}-${month}`}
              data-status={status}
            >
              {isBusy ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : lbl}
            </button>
          );
        })}
      </div>

      {balanceDue > 0 && (
        <div className="mt-1.5 text-[10px] text-slate-500 flex items-center justify-between">
          <span>
            Solde dû : <b className="text-slate-700">{balanceDue.toLocaleString("fr-CA")} $</b>
          </span>
          <span className="text-[9px] italic">1 clic = cycle : à payer → payé → retard → à payer</span>
        </div>
      )}
    </div>
  );
}



const TICKET_PRIORITIES_FORM = [
  { value: "low",    label: "Basse" },
  { value: "normal", label: "Normale" },
  { value: "high",   label: "Haute" },
  { value: "urgent", label: "Urgente" },
];
const TICKET_STATUSES_FORM = [
  { value: "open",        label: "À faire" },
  { value: "in_progress", label: "En cours" },
  { value: "resolved",    label: "Terminé" },
  { value: "closed",      label: "Fermé" },
];
const TICKET_CATEGORIES = [
  "plomberie", "électricité", "chauffage", "ventilation", "serrurerie",
  "peinture", "maçonnerie", "menuiserie", "électroménager", "infestation",
  "extérieur", "autre",
];

function TicketFormModal({ mode, target, unitId, unitLabel, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const t0 = target || {};
  const [form, setForm] = useState({
    title: t0.title || "",
    description: t0.description || "",
    priority: t0.priority || "normal",
    status: t0.status || "open",
    category: t0.category || "",
    resolution_note: t0.resolution_note || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Le titre du ticket est requis"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        status: form.status,
        category: form.category.trim() || null,
        resolution_note: form.resolution_note.trim() || null,
      };
      if (isEdit) {
        await axios.put(`${API}/portal/immeuble/tickets/${target.id}`, payload, { headers: authH() });
      } else {
        await axios.post(`${API}/portal/immeuble/units/${unitId}/tickets`, payload, { headers: authH() });
      }
      toast.success(isEdit ? "Ticket mis à jour" : "Ticket créé");
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail :
        Array.isArray(detail) ? detail.map((x) => `${x.loc?.slice(1).join(".")} : ${x.msg}`).join(" · ") :
        "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="immeuble-ticket-form-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
              {isEdit ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                {isEdit ? "Modifier le ticket" : "Nouveau ticket"}
              </h3>
              <p className="text-[10px] text-slate-500">
                Journal technique · Appartement {unitLabel || "—"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          <FieldLabel label="Titre" required>
            <input value={form.title} onChange={(e) => upd("title", e.target.value)} maxLength={200} placeholder="ex: Fuite robinet cuisine" className="cc-input" data-testid="immeuble-ticket-form-title" />
          </FieldLabel>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FieldLabel label="Priorité">
              <select value={form.priority} onChange={(e) => upd("priority", e.target.value)} className="cc-input" data-testid="immeuble-ticket-form-priority">
                {TICKET_PRIORITIES_FORM.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Statut">
              <select value={form.status} onChange={(e) => upd("status", e.target.value)} className="cc-input" data-testid="immeuble-ticket-form-status">
                {TICKET_STATUSES_FORM.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Catégorie">
              <select value={form.category} onChange={(e) => upd("category", e.target.value)} className="cc-input" data-testid="immeuble-ticket-form-category">
                <option value="">— Aucune —</option>
                {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FieldLabel>
          </div>

          <FieldLabel label="Description">
            <textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)} maxLength={2000} placeholder="Détaillez l'anomalie, le contexte, les actions effectuées…" className="cc-input" data-testid="immeuble-ticket-form-description" />
          </FieldLabel>

          {(form.status === "resolved" || form.status === "closed") && (
            <FieldLabel label="Note de résolution (optionnel)">
              <textarea rows={2} value={form.resolution_note} onChange={(e) => upd("resolution_note", e.target.value)} maxLength={2000} placeholder="ex: Plombier intervenu, joint remplacé." className="cc-input" data-testid="immeuble-ticket-form-resolution" />
            </FieldLabel>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-ticket-form-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Créer le ticket"}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}


// =====================================================================
//  Étape 4 — Tuile Stripe (Méthode A : PAD · Méthode B : Lien Checkout)
// =====================================================================
// StripeActionModal extracted to ./modals/StripeActionModal.jsx (Batch #12 refactor)

// StripeModeBanner extracted to ./modals/shared.jsx (Batch #12 refactor)

// TabBtn extracted to ./modals/shared.jsx (Batch #12 refactor)

// StripePADPanel extracted to ./modals/shared.jsx (Batch #12 refactor)

// StripeCheckoutPanel extracted to ./modals/shared.jsx (Batch #12 refactor)




// =====================================================================
//  Étape 4 — 6 tuiles restantes du Bloc Fixe
//  Lot A (Fichiers) : Coffre-fort + Dépôt Doc
//  Lot B (Légal)    : Bail TAL + RL-31
//  Lot C (Outils)   : Kilométrage + Enquête de crédit
// =====================================================================

function BaseActionModal({ title, subtitle, tone = "indigo", icon: Icon, onClose, children, footer, testId }) {
  const t = ACTION_TONES[tone] || ACTION_TONES.indigo;
  // Batch #6 — Escape pour fermer (UX fix)
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-6" data-testid={testId} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[94vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`px-5 py-3 border-b flex items-center justify-between ${t.bg}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-lg ${t.iconBg} ${t.iconText} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-800 text-sm truncate">{title}</h3>
              {subtitle && <p className="text-[10px] text-slate-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/60 rounded-lg flex-shrink-0">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2 flex-wrap">{footer}</div>}
        <ModalStyles />
      </div>
    </div>
  );
}

// ====================================================================
//  LOT A — UnifiedDocumentDropModal (Coffre-fort + Dépôt rapide unifiés)
//  Pas d'onglet : un seul formulaire. Routage backend automatique :
//   - Catégories structurées (taxes, assurances, évaluation, autre) → safe
//   - Catégories courantes (facture, bail, correspondance)           → drops
// ====================================================================
const DOCUMENT_CATEGORIES = [
  // Documents fixes annuels — Coffre-fort structuré
  { value: "taxes_municipales",        label: "Taxes municipales",         route: "safe", hasAmount: true,  hasDueDate: true,  group: "Fixe (Coffre-fort)", icon: "🏛️", color: "cyan",    short: "Compte de taxes ville" },
  { value: "taxes_scolaires",          label: "Taxes scolaires",           route: "safe", hasAmount: true,  hasDueDate: true,  group: "Fixe (Coffre-fort)", icon: "🎓", color: "violet",  short: "CSDM, CSMB, etc." },
  { value: "assurance_batiment",       label: "Assurance bâtiment",        route: "safe", hasAmount: true,  hasDueDate: true,  group: "Fixe (Coffre-fort)", icon: "🛡️", color: "rose",    short: "Police d'assurance" },
  { value: "assurance_responsabilite", label: "Assurance responsabilité",  route: "safe", hasAmount: true,  hasDueDate: true,  group: "Fixe (Coffre-fort)", icon: "⚖️", color: "indigo",  short: "Responsabilité civile" },
  { value: "evaluation_municipale",    label: "Évaluation municipale",     route: "safe", hasAmount: true,  hasDueDate: false, group: "Fixe (Coffre-fort)", icon: "📐", color: "pink",    short: "Rôle d'évaluation" },
  // Documents courants — Dépôt rapide
  { value: "facture",                  label: "Facture / Reçu",            route: "drop", hasAmount: false, hasDueDate: false, group: "Courant (Dépôt rapide)", icon: "🧾", color: "amber",   short: "Canac, Rona, etc." },
  { value: "bail_doc",                 label: "Bail / Contrat",            route: "drop", hasAmount: false, hasDueDate: false, group: "Courant (Dépôt rapide)", icon: "📋", color: "orange",  short: "Bail, contrat entretien" },
  { value: "correspondance",           label: "Correspondance / Avis",     route: "drop", hasAmount: false, hasDueDate: false, group: "Courant (Dépôt rapide)", icon: "✉️", color: "sky",     short: "Lettre, mise en demeure" },
  // Autre — coffre-fort par défaut (utilisateur ne sait pas où classer)
  { value: "autre",                    label: "Autre document",            route: "safe", hasAmount: true,  hasDueDate: false, group: "Autre",                   icon: "📄", color: "slate",   short: "Je ne sais pas où classer" },
];

// Batch #14.35 — Mapping couleurs tuiles (Tailwind safelist)
const CATEGORY_TONES = {
  cyan:    { bg: "bg-cyan-100",    text: "text-cyan-700",    border: "border-cyan-300",    ring: "ring-cyan-400" },
  violet:  { bg: "bg-violet-100",  text: "text-violet-700",  border: "border-violet-300",  ring: "ring-violet-400" },
  rose:    { bg: "bg-rose-100",    text: "text-rose-700",    border: "border-rose-300",    ring: "ring-rose-400" },
  indigo:  { bg: "bg-indigo-100",  text: "text-indigo-700",  border: "border-indigo-300",  ring: "ring-indigo-400" },
  pink:    { bg: "bg-pink-100",    text: "text-pink-700",    border: "border-pink-300",    ring: "ring-pink-400" },
  amber:   { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-300",   ring: "ring-amber-400" },
  orange:  { bg: "bg-orange-100",  text: "text-orange-700",  border: "border-orange-300",  ring: "ring-orange-400" },
  sky:     { bg: "bg-sky-100",     text: "text-sky-700",     border: "border-sky-300",     ring: "ring-sky-400" },
  slate:   { bg: "bg-slate-100",   text: "text-slate-700",   border: "border-slate-300",   ring: "ring-slate-400" },
};

// Batch #14.150 — Exporté pour PortalImmeubleActionPage
export function UnifiedDocumentDropModal({
  buildings, apartments, selectedBuilding, selectedApt,
  onBuildingChange, onAptChange, onClose, onReload,
}) {
  const currentYear = new Date().getFullYear();
  const [scope, setScope] = useState(selectedApt ? "unit" : "global"); // 'global' ou 'unit'
  const [bId, setBId] = useState(selectedBuilding?.id || buildings[0]?.id || "");
  const [uId, setUId] = useState(selectedApt?.id || "");
  const [category, setCategory] = useState("");
  const [year, setYear] = useState(currentYear);
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  const cat = DOCUMENT_CATEGORIES.find((c) => c.value === category) || null;
  const aptsOfB = apartments.filter((a) => a.property_id === bId);

  // Sync external selection
  useEffect(() => { if (selectedBuilding?.id) setBId(selectedBuilding.id); }, [selectedBuilding?.id]);
  useEffect(() => {
    if (selectedApt?.id) { setUId(selectedApt.id); setScope("unit"); }
  }, [selectedApt?.id]);

  // Load existing safe documents (filtered by building + year)
  const loadDocs = async () => {
    if (!bId) { setDocs([]); return; }
    setDocsLoading(true);
    try {
      const r = await axios.get(`${API}/portal/immeuble/safe`, {
        params: { year, property_id: bId },
        headers: authH(),
      });
      setDocs(r.data?.items || []);
    } catch (e) {
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDocs(); }, [bId, year]);

  const openDoc = async (d) => {
    try {
      const r = await axios.get(`${API}/portal/immeuble/safe/${d.id}/download`, { headers: authH(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = d.filename_original || `${d.label || d.doc_type || "document"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fichier indisponible");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Veuillez sélectionner un fichier"); return; }
    if (!category) { toast.error("Veuillez choisir une catégorie"); return; }
    if (!bId) { toast.error("Veuillez choisir un immeuble"); return; }
    if (scope === "unit" && !uId) { toast.error("Veuillez choisir un appartement"); return; }
    if (cat?.route === "safe" && (!year || year < 2000 || year > 2099)) {
      toast.error("Année invalide"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      if (cat.route === "safe") {
        // Coffre-fort structuré
        fd.append("property_id", bId);
        fd.append("doc_type", category);
        fd.append("year", String(year));
        if (amount) fd.append("amount", String(amount));
        if (dueDate) fd.append("due_date", dueDate);
        if (label) fd.append("label", label);
        if (notes) fd.append("notes", notes);
        await axios.post(`${API}/portal/immeuble/safe/upload`, fd, {
          headers: { ...authH(), "Content-Type": "multipart/form-data" },
        });
        toast.success("Document archivé dans le coffre-fort");
      } else {
        // Dépôt rapide
        const url = scope === "unit"
          ? `${API}/portal/immeuble/units/${uId}/drops`
          : `${API}/portal/immeuble/drops/global`;
        await axios.post(url, fd, {
          headers: { ...authH(), "Content-Type": "multipart/form-data" },
        });
        toast.success("Document déposé — votre équipe a été notifiée");
      }
      // Reset minimal
      setFile(null);
      setLabel("");
      setAmount("");
      setDueDate("");
      setNotes("");
      const inp = document.getElementById("immeuble-doc-file-input");
      if (inp) inp.value = "";
      loadDocs();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Supprimer ce document du coffre-fort ?")) return;
    try {
      await axios.delete(`${API}/portal/immeuble/safe/${id}`, { headers: authH() });
      toast.success("Document supprimé");
      loadDocs();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <BaseActionModal
      title="Documents"
      subtitle="Envoi unifié — votre équipe est notifiée automatiquement"
      tone="emerald"
      icon={FolderOpen}
      testId="immeuble-action-modal-documents"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="immeuble-doc-cancel">
            Fermer
          </button>
          <button
            onClick={submit}
            disabled={busy || !file || !category || !bId || (scope === "unit" && !uId)}
            className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-doc-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Envoyer le document
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Bandeau explicatif */}
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-[11px] leading-snug text-emerald-900">
          <b>Un seul formulaire</b> pour tous vos documents : taxes, assurances, factures, baux, correspondances.
          Nous classons automatiquement selon la catégorie choisie.
        </div>

        {/* Scope : Immeuble vs Appartement */}
        <div>
          <span className="text-[11px] font-semibold text-slate-700 inline-flex items-center gap-1 mb-1.5">
            Associer à<span className="text-rose-500">*</span>
          </span>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setScope("global")}
              className={`px-3 py-2 rounded-lg border text-xs font-semibold transition ${scope === "global" ? "border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="immeuble-doc-scope-global"
            >
              <Building2 className="w-3.5 h-3.5 inline mr-1.5" />Immeuble (global)
            </button>
            <button
              type="button"
              onClick={() => setScope("unit")}
              className={`px-3 py-2 rounded-lg border text-xs font-semibold transition ${scope === "unit" ? "border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="immeuble-doc-scope-unit"
            >
              <DoorOpen className="w-3.5 h-3.5 inline mr-1.5" />Appartement
            </button>
          </div>
        </div>

        {/* Sélecteurs */}
        <div className="grid grid-cols-2 gap-3">
          <FieldLabel label="Immeuble" required>
            <select
              value={bId}
              onChange={(e) => { setBId(e.target.value); setUId(""); onBuildingChange?.(e.target.value); }}
              className="cc-input"
              data-testid="immeuble-doc-building"
            >
              <option value="">— Choisir —</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.label || b.address || b.id}</option>
              ))}
            </select>
          </FieldLabel>
          {scope === "unit" && (
            <FieldLabel label="Appartement" required>
              <select
                value={uId}
                onChange={(e) => { setUId(e.target.value); onAptChange?.(e.target.value); }}
                className="cc-input"
                data-testid="immeuble-doc-unit"
              >
                <option value="">— Choisir —</option>
                {aptsOfB.map((a) => (
                  <option key={a.id} value={a.id}>{a.label || a._raw?.unit_label || a.id}</option>
                ))}
              </select>
            </FieldLabel>
          )}
        </div>

        {/* Catégorie en TUILES + Année — Batch #14.35 */}
        <div>
          <FieldLabel label="Catégorie" required>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="immeuble-doc-category-grid">
              {DOCUMENT_CATEGORIES.map((c) => {
                const tone = CATEGORY_TONES[c.color] || CATEGORY_TONES.slate;
                const isActive = category === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    className={`relative p-3 rounded-xl border text-left transition hover:shadow-md ${
                      isActive
                        ? `${tone.border} ${tone.bg} ring-2 ${tone.ring}`
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`immeuble-doc-category-tile-${c.value}`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${tone.bg} ${tone.text} flex items-center justify-center text-xl mb-2`}>
                      <span>{c.icon}</span>
                    </div>
                    <div className={`text-[12px] font-bold leading-tight ${isActive ? tone.text : "text-slate-800"}`}>
                      {c.label}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">{c.short}</div>
                    {isActive && (
                      <div className={`absolute top-2 right-2 w-5 h-5 rounded-full ${tone.text.replace("text-", "bg-")} text-white flex items-center justify-center text-[10px] font-bold`}>
                        ✓
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Conservé : un select caché qui assure la compatibilité avec d'éventuels tests E2E qui ciblent le data-testid existant */}
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
              data-testid="immeuble-doc-category"
            >
              <option value="">— Choisir une catégorie —</option>
              {DOCUMENT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </FieldLabel>
        </div>

        <FieldLabel label="Année" required>
          <input
            type="number"
            min="2000"
            max="2099"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="cc-input font-mono w-32"
            data-testid="immeuble-doc-year"
          />
        </FieldLabel>

        {/* Champs conditionnels — montant + échéance pour documents fixes */}
        {cat && (cat.hasAmount || cat.hasDueDate) && (
          <div className="grid grid-cols-2 gap-3">
            {cat.hasAmount && (
              <FieldLabel label="Montant (optionnel)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="ex: 2845.50"
                  className="cc-input font-mono"
                  data-testid="immeuble-doc-amount"
                />
              </FieldLabel>
            )}
            {cat.hasDueDate && (
              <FieldLabel label="Date d'échéance (optionnel)">
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="cc-input"
                  data-testid="immeuble-doc-due-date"
                />
              </FieldLabel>
            )}
          </div>
        )}

        {/* Libellé + notes */}
        <FieldLabel label="Libellé (optionnel)">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={120}
            placeholder="ex: Compte de taxes 1er versement"
            className="cc-input"
            data-testid="immeuble-doc-label"
          />
        </FieldLabel>

        <FieldLabel label="Notes (optionnel)">
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            placeholder="Précisions à transmettre à votre équipe"
            className="cc-input"
            data-testid="immeuble-doc-notes"
          />
        </FieldLabel>

        {/* Fichier */}
        <FieldLabel label="Fichier" required>
          <input
            id="immeuble-doc-file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="cc-input text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-emerald-100 file:text-emerald-700 file:font-semibold hover:file:bg-emerald-200"
            data-testid="immeuble-doc-file"
          />
          <p className="text-[10px] text-slate-500 mt-1">PDF, JPG ou PNG · max 25 Mo</p>
        </FieldLabel>

        {/* Liste des documents existants (coffre-fort seulement) — masquée si la catégorie route vers le dépôt rapide */}
        {(!cat || cat.route === "safe") && (
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
              <Inbox className="w-3.5 h-3.5 text-emerald-600" />
              Documents du coffre-fort — {year}
            </h4>
            {docsLoading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
          </div>
          {docs.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">Aucun document archivé pour cet immeuble en {year}.</p>
          ) : (
            <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="immeuble-doc-list">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
                  <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-700 truncate">{d.label || d.filename_original || d.doc_type}</div>
                    <div className="text-[10px] text-slate-500">
                      {d.doc_type}{d.amount ? ` · ${Number(d.amount).toFixed(2)} $` : ""}{d.due_date ? ` · échéance ${d.due_date}` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => openDoc(d)}
                    className="p-1 hover:bg-cyan-100 rounded"
                    title="Ouvrir / télécharger"
                    data-testid={`immeuble-doc-open-${d.id}`}
                  >
                    <FileDown className="w-3 h-3 text-cyan-700" />
                  </button>
                  <button
                    onClick={() => removeDoc(d.id)}
                    className="p-1 hover:bg-rose-100 rounded"
                    title="Supprimer"
                    data-testid={`immeuble-doc-delete-${d.id}`}
                  >
                    <Trash2 className="w-3 h-3 text-rose-500" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </div>
    </BaseActionModal>
  );
}




// ====================================================================
//  LOT B — Bail TAL (formulaire MVP : champs essentiels)
// ====================================================================

function CheckboxChip({ label, checked, onChange, dataTestId }) {
  return (
    <label className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] cursor-pointer ${checked ? "bg-blue-50 border-blue-300 text-blue-800" : "bg-white border-slate-200 text-slate-600"}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-3 h-3" data-testid={dataTestId} />
      {label}
    </label>
  );
}

// ====================================================================
//  Mini barre de progression — polling /leases/{id}/status — Batch #4
// ====================================================================
// LEASE_PROCESSING_STEPS + LeaseProcessingProgress moved to ./modals/shared.jsx (Batch #13.1 fix)



// ====================================================================
//  LOT B — RL-31 (Batch #11 — Génération directe + Demande admin)
// ====================================================================
// Rl31ActionModal extracted to ./modals/Rl31ActionModal.jsx (Batch #12 refactor)

// ====================================================================
//  LOT C — Kilométrage
// ====================================================================
// MileageActionModal extracted to ./modals/MileageActionModal.jsx (Batch #12 refactor)

// ====================================================================
//  LOT C — Enquête de crédit
// ====================================================================
// CreditCheckActionModal extracted to ./modals/CreditCheckActionModal.jsx (Batch #12 refactor)





// ====================================================================
//  Modale Renouvellements — proprio saisit la hausse de loyer
// ====================================================================
// RentIncreaseActionModal extracted to ./modals/RentIncreaseActionModal.jsx (Batch #12 refactor)


// ====================================================================
// ====================================================================
//  Modale Journal — Conciliation manuelle (Batch #5)
//  Extracted to ./modals/JournalActionModal.jsx (Batch #12 refactor)
//  Constants JOURNAL_PAYMENT_METHODS + MONTHS_FR moved to JournalActionModal.jsx
//  (Batch #14.1 — fix orphan refs causing white screen)
// ====================================================================

// JournalActionModal extracted to ./modals/JournalActionModal.jsx (Batch #12 refactor)




// ============================================================
// Batch #10 — ReceiptsActionModal : Quittances de loyer
// ============================================================
// ReceiptsActionModal extracted to ./modals/ReceiptsActionModal.jsx (Batch #12 refactor)
